import { UseConfirmationToast } from '@contexts/ConfirmationToast';
import { useToast } from '@contexts/Toast';
import { faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';
import { faTrashCan } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Axios } from '@helpers/ApiService';
import {
  errorMessage,
  OrderCreationMasterData,
  ProductType,
  responseTexts,
  WeightType,
} from '@helpers/Constants';
import { ApiEndpoints, FrontendRoutes } from '@helpers/Constants/Navigation';
import {
  Addon,
  AddonsList,
  CheckinOrderProps,
  CustomizeOrderProps,
  License,
  MasterPackageExtens,
  MasterProduct,
  NewOrderInitiationProps,
  OrderPackage,
  OrderServiceColors,
  OrderServiceProps,
  OrderSpeciesData,
  ProductDetails,
  ProductDraft,
  ProductEntry,
  ProductTypeInfo,
  RequireFlag,
  NewOrderSelectedProduct,
  Species,
  MasterSpeciesProps,
} from '@helpers/Models';
import { useInputValidation } from '@hooks/useInputValidation';
import { CustomerOrderService } from '@services/CustomerService';
import { formatDate, getServiceDeposit } from '@services/OrderCreationService';
import { useState, useRef, useEffect, KeyboardEvent } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import OrderSplitView from './OrderSplitView';
import { AddOrEditLicense } from '@components/Modal/LicenseDetails/AddOrEditLicense';
import { ConvertToStorageFormat } from '@helpers/ConverToStorageFormat';
import { AddNewProductModal } from '@components/Modal/NewOrder/AddNewProductModal';
import { useCreditAmount } from '@contexts/CreditAmountContext';
import { CopyProductModal } from '@components/Modal/NewOrder/CopyProductModal';
import { EditProductModal } from '@components/Modal/NewOrder/EditProductModal';

const SpeciesDetails: React.FC = () => {
  const hasInitializedColors = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();
  const isEditOrder = location.state?.from?.orderDetails === true;
  const orderId = location.state?.orderId;

  const { showConfirmationToast } = UseConfirmationToast();
  const { showToastMessage } = useToast();

  const [orderData, setOrderData] = useState<CustomizeOrderProps>({
    customerId: 0,
    butcherSpeciesId: 0,
    speciesId: 0,
    quantity: 0,
    speciesName: '',
    species: [],
  });

  const [currentCustomizingId, setCurrentCustomizingId] = useState<number>(1);
  const [customerDetails, setCustomerDetails] =
    useState<NewOrderInitiationProps>();

  const [customerId, setCustomerId] = useState<number>(0);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const [checkinOpen, setCheckinOpen] = useState(false);
  const [serviceOpen, setServiceOpen] = useState(false);
  const [productOpen, setProductOpen] = useState(false);
  const [addNewProduct, setAddNewProduct] = useState(false);

  const [openRepeatSpecies, setOpenRepeatSpecies] = useState(false);
  const dropdownRefs = useRef<Map<number, HTMLDivElement>>(new Map());

  const [openAddons, setOpenAddons] = useState(false);
  const [editingLicense, setEditingLicense] = useState<License | null>(null);

  // Checkin states
  const [requireFlag, setRequireFlag] = useState<RequireFlag>({
    citesFlag: false,
    PermitFlag: false,
    LicenseFlag: false,
    LengthFlag: false,
  });
  const [showLicenseForm, setShowLicenseForm] = useState<boolean>(false);
  const [checkinOrderData, setCheckinOrderData] =
    useState<CheckinOrderProps | null>({
      weight: '',
      permitNumber: '',
      citesNumber: '',
      conformationNumber: '',
      notes: '',
      huntingLicenceId: 0,
      feet: 0,
      inches: 0,
      licenseNumber: '',
      harvestStateId: 0,
      stateName: '',
    });
  const [serviceStatusMap, setServiceStatusMap] = useState<
    Record<number, number>
  >({});
  const [productStatusMap, setProductStatusMap] = useState<
    Record<number, number>
  >({});

  // Input validation hooks for feet and inches
  const feetValidation = useInputValidation('number', '');
  const inchesValidation = useInputValidation('number', '');

  const [selectedLicenseId, setSelectedLicenseId] = useState<number | null>(
    null,
  );
  const today = new Date();
  const validLicenses =
    customerDetails?.HuntingLicences?.filter((license) => {
      if (!license?.expiryDate) return false;

      const expiry = new Date(license.expiryDate);
      return expiry >= today;
    }) || [];

  // Service states
  const [filteredServices, setFilteredServices] = useState<OrderServiceProps[]>(
    [],
  );
  const [openServiceMenuId, setOpenServiceMenuId] = useState<number | null>(
    null,
  );
  const [productType, setProductType] = useState<ProductTypeInfo>({
    productType: 1,
    minValue: 1,
  });
  const [searchKey, setSearchKey] = useState<string>('');
  const [openColorServiceId, setOpenColorServiceId] = useState<number | null>(
    null,
  );
  const [serviceSearch, setServiceSearch] = useState('');
  const [services, setServices] = useState<OrderServiceProps[]>([]);
  const [colors, setColors] = useState<OrderServiceColors[]>([]);
  const [selectedServiceIds, setSelectedServiceIds] = useState<number[]>([]);
  // const [serviceIdMap, setServiceIdMap] = useState<Record<number, number>>({});

  const [selectedColors, setSelectedColors] = useState<{
    [key: number]: number;
  }>({});

  // Product States
  const [productDraft, setProductDraft] = useState<ProductDraft | null>({
    productId: null,
    name: '',
    price: 0,
    fatPercentage: '',
    quantity: '',
    instruction: '',
    packages: [],
    addons: [],
    notes: '',
  });
  const [editProductModal, setEditProductModal] = useState(false);
  const [copyProductModal, setCopyProductModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<ProductDraft | null>(
    null,
  );
  const [selectedProductIndex, setSelectedProductIndex] = useState<
    number | null
  >(null);

  const [masterProducts, setMasterProducts] = useState<MasterProduct[]>([]);
  const [currentProductDetail, setCurrentProductDetail] =
    useState<ProductDetails | null>(null);
  const [productSearch, setProductSearch] = useState('');
  const [filteredProducts, setFilteredProducts] = useState(masterProducts);
  const [filteredAddons, setFilteredAddons] = useState<AddonsList[]>([]);

  const [addedProducts, setAddedProducts] = useState<ProductDraft[]>([]);
  const [addons, setAddons] = useState<AddonsList[]>([]);
  const [MasterPackageExtenss, setMasterPackageExtenss] = useState<
    MasterPackageExtens[]
  >([]);

  const [percentageError, setPercentageError] = useState<boolean>(false);

  const [selectedPackages, setSelectedPackages] = useState<OrderPackage[]>(
    MasterPackageExtenss.filter((p) => p.defaultFlag === 1).map((p) => ({
      ...p,
      name: p.packageType,
      percentage: '',
    })),
  );

  // const [speciesList, setSpeciesList] = useState<{ id: number; speciesName: string }[]>([]);

  const [dropdownOpenIndex, setDropdownOpenIndex] = useState<number | null>(
    null,
  );
  const [editOrderData, setEditOrderData] = useState<any>({});

  const remainingPackages = MasterPackageExtenss.filter(
    (p) => !selectedPackages.some((sp) => sp.id === p.id),
  );

  const { creditAmount, setCreditAmount } = useCreditAmount();

  useEffect(() => {
    const currentCustomizingId = localStorage.getItem('currentCustomizingId');
    if (currentCustomizingId) {
      setCurrentCustomizingId(Number(currentCustomizingId));
    }
  }, []);

  /**
   * Effect to handle closing the service menu when clicking outside.
   *
   * Adds a global click event listener when a service menu is open.
   * Any click on the document will reset `openServiceMenuId` to null,
   * effectively closing the currently open menu.
   *
   * The event listener is removed:
   *  - when the component unmounts
   *  - or when `openServiceMenuId` changes
   *
   * Dependency:
   * @dependency openServiceMenuId - Determines whether the listener should be active.
   *
   * @note
   * This implementation closes the menu on any click, including clicks inside the menu.
   * To restrict this to only outside clicks, you may need to use a ref and
   * check `event.target` against the menu element.
   */
  useEffect(() => {
    const handleClickOutside = () => setOpenServiceMenuId(null);
    if (openServiceMenuId !== null) {
      document.addEventListener('click', handleClickOutside);
    }
    return () => document.removeEventListener('click', handleClickOutside);
  }, [openServiceMenuId]);

  /**
   * Effect to initialize credit amount when editing an existing order.
   *
   * On component mount, this checks if the page is in edit mode (`isEditOrder`).
   * If true, it retrieves stored order data from `localStorage`, extracts the
   * relevant species using `customerId` and `currentCustomizingId`, and sets
   * the credit amount accordingly.
   *
   * Data Sources:
   * - editOrderData (localStorage): Full saved order data
   * - customerId (localStorage): Current customer identifier
   * - currentCustomizingId (localStorage): Index of the selected species
   *
   * @dependency []
   * - Empty dependency array ensures this runs only once on component mount
   *
   * @note
   * - Assumes `editOrderData` structure is valid JSON
   * - Uses optional chaining to safely access nested properties
   * - Defaults:
   *    customerId → 1 (if null)
   *    creditAmount → 0 (if undefined)
   */
  useEffect(() => {
    if (isEditOrder) {
      const raw = localStorage.getItem('editOrderData');
      if (raw) {
        const all = JSON.parse(raw);
        setEditOrderData(all);
        const customerId = localStorage.getItem('customerId');
        const currentCustomizingId = Number(
          localStorage.getItem('currentCustomizingId') ?? '0',
        );
        const currentSpecies =
          all[customerId ?? 1]?.species?.[currentCustomizingId];
        // Initialize with existing creditAmount from localStorage
        setCreditAmount(currentSpecies?.creditAmount ?? 0);
      }
    }
  }, []);

  /**
   * Effect to hydrate and initialize UI state from localStorage
   * based on the current customizing session.
   *
   * This runs whenever core dependencies (order data, master data, etc.)
   * are available and reconstructs the full UI state for services,
   * products, check-in details, and related mappings.
   *
   * Data Sources:
   * - localStorage:
   *    - currentCustomizingId → index of current species
   *    - orderData / editOrderData → persisted customization data
   *
   * @dependency
   * - orderData
   * - addons
   * - MasterPackageExtenss
   * - colors
   * - masterProducts
   */
  useEffect(() => {
    const id = Number(localStorage.getItem('currentCustomizingId') ?? '0');
    setCurrentCustomizingId(id);

    const storageKey = isEditOrder ? 'editOrderData' : 'orderData';
    const existing = localStorage.getItem(storageKey);
    if (existing) {
      const allCustomizations = JSON.parse(existing);
      const customerId = String(orderData?.customerId);
      const savedSpecies = allCustomizations[customerId]?.species?.[id];

      if (savedSpecies) {
        const statusMap: Record<number, number> = {};
        savedSpecies.services?.forEach(
          (service: { butcherServiceId: number; status: number }) => {
            statusMap[service.butcherServiceId] = service.status ?? 0;
          },
        );
        setServiceStatusMap(statusMap);

        const productStatus: Record<number, number> = {};
        savedSpecies.products?.forEach(
          (product: { productId: number; status: number }) => {
            productStatus[product.productId] = product.status ?? 0;
          },
        );
        setProductStatusMap(productStatus);

        setCheckinOrderData(
          (savedSpecies.checkIn as unknown as CheckinOrderProps) ?? null,
        );
        setSelectedLicenseId(savedSpecies.checkIn?.huntingLicenceId ?? null);
        feetValidation.setValue(String(savedSpecies.checkIn?.feet ?? ''));
        inchesValidation.setValue(String(savedSpecies.checkIn?.inches ?? ''));
        setSelectedServiceIds(
          savedSpecies.services?.map(
            (service: { butcherServiceId: number; serviceId: number }) =>
              service.butcherServiceId ?? service.serviceId,
          ) ?? [],
        );

        if (isEditOrder && colors.length > 0 && !hasInitializedColors.current) {
          const colorMap =
            savedSpecies.services?.reduce(
              (
                acc: Record<number, number>,
                service: {
                  colorId: number | null | undefined;
                  butcherServiceId: number;
                  serviceId: number;
                },
              ) => {
                if (service.colorId !== undefined && service.colorId !== null) {
                  acc[service.butcherServiceId ?? service.serviceId] =
                    service.colorId;
                }
                return acc;
              },
              {} as Record<number, number>,
            ) ?? {};

          setSelectedColors(colorMap);
          hasInitializedColors.current = true;
        }

        setSelectedColors(
          savedSpecies.services?.reduce(
            (acc: Record<number, number>, s: any) => {
              if (s.colorId !== undefined && s.colorId !== null) {
                acc[s.butcherServiceId ?? s.serviceId] = s.colorId;
              }
              return acc;
            },
            {},
          ) ?? {},
        );
        setAddedProducts(
          savedSpecies.products?.map(
            (product: OrderSpeciesData['products'][number]) => ({
              productId: product.productId,
              butcherProductId: product.butcherProductId,
              name:
                masterProducts.find((mp) => mp.id === product.productId)
                  ?.name ??
                masterProducts.find((mp) => mp.id === product.butcherProductId)
                  ?.name ??
                '',
              price:
                masterProducts.find((mp) => mp.id === product.productId)
                  ?.price ?? 0,
              fatPercentage: String(product.fatPercent),
              quantity: String(product.quantity),
              instruction: product.packagingNotes ?? '',
              status: product.status ?? 0,
              addons:
                product.addOnIds
                  ?.map((addonId: number) =>
                    addons.find((a) => a.id === addonId),
                  )
                  .filter(
                    (addon: AddonsList | undefined): addon is AddonsList =>
                      Boolean(addon),
                  ) ?? [],
              packages:
                product.packaging?.map(
                  (
                    pkg: OrderSpeciesData['products'][number]['packaging'][number],
                  ) => {
                    const masterPkg = MasterPackageExtenss.find(
                      (mp) => mp.id === pkg.productPackageTypeId,
                    );
                    return {
                      id: pkg.productPackageTypeId,
                      packageType:
                        (pkg as any).packageType ??
                        masterPkg?.packageType ??
                        '',
                      packageSize:
                        (pkg as any).size ?? masterPkg?.packageSize ?? 0,
                      unit: masterPkg?.unit ?? '',
                      percentage: normalizePackagePercentageString(
                        pkg.percentage,
                      ),
                    };
                  },
                ) ?? [],
              notes: product.packagingNotes ?? '',
            }),
          ) ?? [],
        );
      }
    }
  }, [orderData, addons, masterProducts, MasterPackageExtenss, colors]);

  /**
   * Effect to fetch master data required for order customization.
   *
   * Triggers API calls to load:
   * - Services
   * - Products
   * - Add-ons
   * - Package types
   *
   * @conditions
   * - `orderData.speciesId` must be valid (non-zero)
   * - In edit mode, `orderData.butcherSpeciesId` must be defined
   *
   * @dependency
   * - orderData.speciesId
   * - orderData.butcherSpeciesId
   *
   */
  useEffect(() => {
    if (!orderData?.speciesId || orderData.speciesId === 0) return;

    // if (isEditOrder && orderData.butcherSpeciesId == undefined) return;

    fetchServiceMaster();
    fetchMasterProducts();
    fetchAddons();
    fetchMasterPackage();
  }, [orderData?.butcherSpeciesId, orderData?.speciesId]);

  useEffect(() => {
    if (MasterPackageExtenss?.length) {
      const defaultPack: OrderPackage[] = MasterPackageExtenss.filter(
        (p) => p.defaultFlag === 1,
      ).map((p) => ({ ...p, name: p.packageType, percentage: '' }));
      setSelectedPackages(defaultPack);
    }
  }, [MasterPackageExtenss]);

  /**
   * Copies ("Same As") data from a selected source species and
   * populates the current form state with it.
   *
   * This allows users to quickly duplicate configuration such as:
   * - Check-in details
   * - Selected services
   * - Service colors
   * - Added products (including addons and packaging)
   *
   * @param sourceSpeciesIndex - Index of the species in `orderData.species`
   *                             to copy data from
   */
  const handleSameAs = (sourceSpeciesIndex: number) => {
    // Get the source species data from orderData
    const sourceSpecies = orderData?.species?.[sourceSpeciesIndex];

    if (!sourceSpecies) return;

    // Populate form states with source species data
    setCheckinOrderData(
      sourceSpecies.checkIn
        ? ({
          weight: String(sourceSpecies.checkIn.weight ?? ''),
          permitNumber: String(sourceSpecies.checkIn.permitNumber ?? ''),
          citesNumber: String(sourceSpecies.checkIn.citesNumber ?? ''),
          conformationNumber: String(
            sourceSpecies.checkIn.conformationNumber ?? '',
          ),
          notes: String(sourceSpecies.checkIn.notes ?? ''),
          huntingLicenceId: Number(
            sourceSpecies.checkIn.huntingLicenceId ?? 0,
          ),
          feet: Number(sourceSpecies.checkIn.feet ?? 0),
          inches: Number(sourceSpecies.checkIn.inches ?? 0),
          licenseNumber: String(sourceSpecies.checkIn.licenseNumber ?? ''),
          harvestStateId: Number(sourceSpecies.checkIn.harvestStateId ?? 0),
        } satisfies CheckinOrderProps)
        : null,
    );

    setSelectedLicenseId(sourceSpecies.checkIn?.huntingLicenceId ?? null);
    feetValidation.setValue(String(sourceSpecies.checkIn?.feet ?? ''));
    inchesValidation.setValue(String(sourceSpecies.checkIn?.inches ?? ''));

    setSelectedServiceIds(
      sourceSpecies.services?.map((s: { serviceId: number }) => s.serviceId) ??
      [],
    );

    setSelectedColors(
      sourceSpecies.services?.reduce((acc: Record<number, number>, s: any) => {
        if (s.colorId !== undefined && s.colorId !== null) {
          acc[s.serviceId] = s.colorId;
        }
        return acc;
      }, {}) ?? {},
    );

    setAddedProducts(
      sourceSpecies.products?.map((product: any) => {
        const masterProduct = masterProducts.find(
          (mp) => mp.id === product.productId,
        );
        return {
          productId: product.productId,
          name: masterProduct?.name ?? '',
          price: masterProduct?.price ?? 0,
          fatPercentage: product.fatPercent,
          quantity: product.quantity,
          instruction: product.packagingNotes ?? '',
          addons:
            product.addOnIds
              ?.map((id: number) => addons.find((a) => a.id === id))
              .filter(
                (addon: AddonsList | undefined): addon is AddonsList =>
                  Boolean(addon),
              ) ?? [],
          packages:
            product.packaging?.map(
              (
                pkg: OrderSpeciesData['products'][number]['packaging'][number],
              ) => {
                const masterPkg = MasterPackageExtenss.find(
                  (mp) => mp.id === pkg.productPackageTypeId,
                );
                return {
                  ...masterPkg,
                  percentage: pkg.percentage,
                };
              },
            ) ?? [],
          notes: product.packagingNotes ?? '',
        };
      }) ?? [],
    );
    setOpenRepeatSpecies(false);

    // Show a success toast
    showToastMessage(
      responseTexts.REPEAT_SPECIES_CONFIGURATION_SUCCESS,
      'success',
    );

    // Open all the check-in, services and products accordion
    setCheckinOpen(true);
    setServiceOpen(true);
    setProductOpen(true);
  };

  const otherSpecies = Array.from({ length: currentCustomizingId }, (_, i) => ({
    index: i,
    label: `${orderData?.speciesName} - ${i + 1}`,
  }));

  const handleSelectPackage = (pack: MasterPackageExtens) => {
    setSelectedPackages((prev) => {
      if (prev.some((p) => p.id === pack.id)) return prev;

      const updated = [
        ...prev,
        { ...pack, name: pack.packageType, percentage: '' },
      ];

      if (updated.length === 2) {
        updated[0] = { ...updated[0], percentage: '50' };
        updated[1] = { ...updated[1], percentage: '50' };
      }

      return updated;
    });

    setProductDraft((prev: ProductDraft | null): ProductDraft | null => {
      if (!prev) return prev;

      const exists = prev.packages?.some((p) => p.id === pack.id);
      if (exists) return prev;

      let updatedPackages: OrderPackage[] = [
        ...(prev.packages || []),
        { ...pack, name: pack.packageType, percentage: '' },
      ];

      // Auto fill when 2 packages
      if (updatedPackages.length === 2) {
        updatedPackages = updatedPackages.map((p) => ({
          ...p,
          name: p.packageType,
          percentage: '50',
        }));
      }

      return {
        ...prev,
        packages: updatedPackages,
      };
    });

    setDropdownOpenIndex(null);
  };

  const handleDeletePackage = (packId: number) => {
    // Prevent deleting the last package
    if (selectedPackages.length <= 1) return;

    setSelectedPackages((prev) => {
      const updated = prev.filter((p) => p.id !== packId);

      if (updated.length === 1) {
        updated[0] = { ...updated[0], percentage: '100' };
      }

      return updated;
    });

    setProductDraft((prev: ProductDraft | null): ProductDraft | null => {
      if (!prev) return prev;

      let updatedPackages = prev.packages?.filter((p) => p.id !== packId);

      if (updatedPackages.length === 1) {
        updatedPackages = updatedPackages.map((p) => ({
          ...p,
          percentage: '100',
        }));
      }

      return {
        ...prev,
        packages: updatedPackages,
      };
    });
  };

  const handleProductSearch = (value: string) => {
    setProductSearch(value);

    const trimmedValue = value.trim().toLowerCase();

    if (!trimmedValue) {
      setFilteredProducts(masterProducts);
      return;
    }

    const filtered = masterProducts.filter((product) =>
      product.name.toLowerCase().includes(trimmedValue),
    );

    setFilteredProducts(filtered);
  };

  const handleAddonSearch = (value: string) => {
    setSearchKey(value);

    const trimmedValue = value.trim().toLowerCase();

    if (!trimmedValue) {
      setFilteredAddons(addons);
      return;
    }

    const filtered = addons.filter((addon) =>
      addon.name.toLowerCase().includes(trimmedValue),
    );

    setFilteredAddons(filtered);
    setOpenAddons(true);
  };

  const normalizePackagePercentageNumber = (value: unknown): number => {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) return 0;
    return Math.max(0, Math.min(100, Math.round(parsed)));
  };

  const normalizePackagePercentageString = (value: unknown): string => {
    return String(normalizePackagePercentageNumber(value));
  };

  const handlePercentageBlur = (value: string) => {
    const num = Number(value);

    if (isNaN(num) || !Number.isInteger(num) || num <= 0 || num >= 100) {
      setPercentageError(true);
      showToastMessage(errorMessage.ORDER_PACKAGE_PERCENTAGE_INVALID, 'error');
    } else {
      setPercentageError(false);
    }
  };

  const handlePercentageChange = (index: number, value: string) => {
    const numericValue = value.replace(/[^0-9]/g, '');
    const num = Math.max(0, Math.min(100, Number(numericValue || 0)));
    const clampedValue = numericValue === '' ? '' : String(num);

    setSelectedPackages((prev) => {
      const updated = [...prev];

      updated[index] = {
        ...updated[index],
        percentage: clampedValue,
      };

      if (updated.length === 2) {
        const otherIndex = index === 0 ? 1 : 0;
        updated[otherIndex] = {
          ...updated[otherIndex],
          percentage: (100 - num).toString(),
        };
      }

      setProductDraft((draft: ProductDraft | null): ProductDraft | null => {
        if (!draft) return draft;
        return { ...draft, packages: [...updated] };
      });

      return updated;
    });
  };

  const handlePercentageKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    const allowedKeys = [
      'Backspace',
      'Delete',
      'Tab',
      'ArrowLeft',
      'ArrowRight',
      'Home',
      'End',
    ];

    if (allowedKeys.includes(e.key)) return;
    if (!/^[0-9]$/.test(e.key)) {
      e.preventDefault();
    }
  };

  /**
   * Handles deletion of a service from the split view, with special handling for deposits.
   *
   * - Maps the provided service ID to its corresponding internal ID.
   * - Retrieves any associated deposit amount for the service.
   *
   * Behavior:
   * - If a deposit exists (> 0):
   *   - Prompts the user with a confirmation explaining that the deposit
   *     will be transferred to the customer’s credit balance.
   *   - On confirmation:
   *     - Adds the deposit amount to the existing credit balance.
   *     - Updates the credit amount in both state and persisted edit order data.
   *     - Removes the selected service.
   *     - Displays a success toast confirming the credit update.
   *
   * - If no deposit exists:
   *   - Prompts a simple confirmation before removing the service.
   *   - On confirmation, directly deletes the service.
   *
   * Parameters:
   * @param serviceId - The ID of the service to be removed.
   * @param setIndex - The fallback index used when customizing ID is unavailable.
   *
   * Dependencies:
   * @dependency getMappedServiceId - Maps external service ID to internal ID.
   * @dependency getServiceDeposit - Retrieves deposit amount for the service.
   * @dependency creditAmount / setCreditAmount - Manages customer credit balance.
   * @dependency editOrderData - Stores editable order data.
   * @dependency handleDeleteService - Performs the actual service removal.
   * @dependency showConfirmationToast - Displays confirmation prompts.
   * @dependency showToastMessage - Displays success feedback.
   */
  const handleSplitViewServiceDelete = (
    serviceId: number,
    setIndex: number,
  ): void => {
    const mappedServiceId = getMappedServiceId(serviceId);

    const depositAmount = getServiceDeposit(
      mappedServiceId,
      customerId,
      currentCustomizingId,
    );
    console.log({
      mappedServiceId,
      customerId,
      currentCustomizingId,
      depositAmount,
    });

    if (depositAmount > 0) {
      showConfirmationToast(
        "Are you sure you want to remove this service? Any deposit paid for this service will be transferred to the customer's credit balance.",
        'Remove Service',
        'delete',
        () => {
          const newCreditAmount = creditAmount + depositAmount;
          setCreditAmount(newCreditAmount);
          if (!editOrderData) return;
          editOrderData[customerId].species[currentCustomizingId].creditAmount =
            newCreditAmount;
          localStorage.setItem('editOrderData', JSON.stringify(editOrderData));
          handleDeleteService(
            serviceId,
            currentCustomizingId ? currentCustomizingId : setIndex,
          );
          setTimeout(() => {
            showToastMessage(
              "The amount has been added to the customer's credit balance successfully.",
              'success',
            );
          }, 300);
        },
        'Remove Service',
        () => { },
        'Cancel',
      );
    } else {
      showConfirmationToast(
        'Are you sure you want to remove this service?',
        'Remove Service',
        'delete',
        () =>
          handleDeleteService(
            serviceId,
            currentCustomizingId ? currentCustomizingId : setIndex,
          ),
        'Remove Service',
        () => { },
        'Cancel',
      );
    }
  };

  const handleDeleteService = (serviceId: number, setIndex: number) => {
    if (!isStorageBased(setIndex)) {
      setSelectedServiceIds((prev) => prev.filter((id) => id !== serviceId));
      return;
    }

    const storageKey = isEditOrder ? 'editOrderData' : 'orderData';
    const raw = localStorage.getItem(storageKey);
    if (!raw) return;
    const all = JSON.parse(raw);
    const customerId = String(orderData?.customerId);

    const services = all[customerId].species[setIndex].services;

    // Find the service being deleted to get its originalServiceId
    const serviceToDelete = services.find(
      (s: { butcherServiceId: number; serviceId: number }) =>
        s.butcherServiceId === serviceId || s.serviceId === serviceId,
    );

    // Remove from services by butcherServiceId
    all[customerId].species[setIndex].services = services.filter(
      (s: { butcherServiceId: number; serviceId: number }) =>
        s.butcherServiceId !== serviceId && s.serviceId !== serviceId,
    );

    // Track deleted originalServiceId
    if (isEditOrder && serviceToDelete) {
      const originalServiceId =
        serviceToDelete.originalServiceId ?? serviceToDelete.serviceId;

      // Only track if it's an existing service (has originalServiceId), not a newly added one
      if (originalServiceId) {
        const deletedServiceIds =
          all[customerId].species[setIndex].deletedServiceIds ?? [];
        if (!deletedServiceIds.includes(originalServiceId)) {
          deletedServiceIds.push(originalServiceId);
        }
        all[customerId].species[setIndex].deletedServiceIds = deletedServiceIds;
      }
    }

    localStorage.setItem(storageKey, JSON.stringify(all));

    if (setIndex === currentCustomizingId) {
      setSelectedServiceIds((prev) => prev.filter((id) => id !== serviceId));
    }
  };

  /**
   * Refreshes customer details from localStorage.
   * This is useful when customer data is updated (e.g., new license added)
   * and we need to refresh the component state.
   */
  const refreshCustomerDetails = () => {
    const updatedCustomerDetails = CustomerOrderService.getCustomerDetails();
    setCustomerDetails(updatedCustomerDetails);
  };

  /**
   * Determines whether a given species set exists in persisted storage.
   *
   * Checks if the species at the provided index is present in localStorage
   * under the current customer’s data.
   *
   * @param setIndex - Index of the species set to check
   *
   * @returns boolean
   * - true  → species exists in storage (storage-based)
   * - false → species does not exist (UI-only / temporary)
   *
   */
  const isStorageBased = (setIndex: number): boolean => {
    const storageKey = isEditOrder ? 'editOrderData' : 'orderData';
    const raw = localStorage.getItem(storageKey);
    if (!raw) return false;
    const all = JSON.parse(raw);
    const customerId = String(orderData?.customerId);
    return !!all[customerId]?.species?.[setIndex];
  };

  /**
   * Updates a product within a species set, handling both
   * local UI state and persisted storage.
   *
   * @param updatedProduct - The modified product data from the form
   * @param index - Index of the product within the species
   * @param setIndex - Index of the species set containing the product
   *
   */
  const handleEditProduct = (
    updatedProduct: ProductDraft,
    index: number,
    setIndex: number,
  ) => {
    if (!isStorageBased(setIndex)) {
      setAddedProducts((prev) =>
        prev.map((p, i) => (i === index ? updatedProduct : p)),
      );
      return;
    }
    const storageKey = isEditOrder ? 'editOrderData' : 'orderData';
    const raw = localStorage.getItem(storageKey);
    if (!raw) return;
    const all = JSON.parse(raw);
    const customerId = String(orderData?.customerId);
    all[customerId].species[setIndex].products[index] =
      ConvertToStorageFormat(updatedProduct);
    localStorage.setItem(storageKey, JSON.stringify(all));

    // update state only if it's the currently active species
    if (setIndex === currentCustomizingId) {
      setAddedProducts((prev) =>
        prev.map((p, i) => (i === index ? updatedProduct : p)),
      );
    }

    showToastMessage('Changes saved successfully.', 'success');
  };

  /**
   * Handles deletion of a product from a species set,
   * supporting both UI-only and storage-backed scenarios.
   *
   * @param index - Index of the product to be removed
   * @param setIndex - Index of the species set containing the product
   * @param butcherProductId - Optional original product ID (used in edit mode tracking)
   *
   */
  const handleDeleteProduct = (index: number, setIndex: number) => {
    if (!isStorageBased(setIndex)) {
      setAddedProducts((prev) => prev.filter((_, i) => i !== index));
      return;
    }

    const storageKey = isEditOrder ? 'editOrderData' : 'orderData';
    const raw = localStorage.getItem(storageKey);
    if (!raw) return;
    const all = JSON.parse(raw);
    const customerId = String(orderData?.customerId);
    const products = all[customerId].species[setIndex].products;

    // Track deleted productId before removing
    if (isEditOrder) {
      const deletedProduct = products[index];
      const idToTrack = deletedProduct?.productId;
      if (idToTrack && !deletedProduct?.isNew) {
        const deletedProductIds =
          all[customerId].species[setIndex].deletedProductIds ?? [];
        if (!deletedProductIds.includes(idToTrack)) {
          deletedProductIds.push(idToTrack);
        }
        all[customerId].species[setIndex].deletedProductIds = deletedProductIds;
      }
    }

    products.splice(index, 1);
    localStorage.setItem(storageKey, JSON.stringify(all));

    if (setIndex === currentCustomizingId) {
      setAddedProducts((prev) => prev.filter((_, i) => i !== index));
    }
  };

  const handleDuplicateProduct = (product: ProductDraft, setIndex: number) => {
    if (!isStorageBased(setIndex)) {
      setAddedProducts((prev) => [...prev, { ...product }]);
      return;
    }
    const storageKey = isEditOrder ? 'editOrderData' : 'orderData';
    const raw = localStorage.getItem(storageKey);
    if (!raw) return;
    const all = JSON.parse(raw);
    const customerId = String(orderData?.customerId);
    all[customerId].species[setIndex].products.push(
      ConvertToStorageFormat(product),
    );
    localStorage.setItem(storageKey, JSON.stringify(all));

    if (setIndex === currentCustomizingId) {
      setAddedProducts((prev) => [...prev, { ...product }]);
    }

    showToastMessage(`${product.name} added to cart`, 'success');
  };

  const handleUpdateCompletedSet = (
    setIndex: number,
    updatedProducts: NewOrderSelectedProduct[],
  ) => {
    const parsedOrderData = CustomerOrderService.getParsedOrderData();
    const customerOrder = parsedOrderData[Number(customerDetails?.id)];

    const updatedOrderData = {
      ...parsedOrderData,
      [Number(customerDetails?.id)]: {
        ...customerOrder,
        species: customerOrder.species.map((s: any, i: number) =>
          i === setIndex ? { ...s, products: updatedProducts } : s,
        ),
      },
    };
    CustomerOrderService.setParsedOrderData(updatedOrderData);
  };

  /**
   * Asynchronously fetches product master data from the server and updates local state.
   *
   * Makes a POST request to the `FETCH_SPECIES_MASTERDATA` API endpoint with the
   * specified master data type (`PRODUCT_MASTER_DATA`). On success:
   * - Updates both `masterProducts` and `filteredProducts` state with the fetched data.
   *
   * On failure:
   * - Displays an error toast with a predefined error message.
   * - Rethrows the error for potential upstream handling.
   *
   * @returns {Promise<void>} A promise that resolves when data fetching and state updates complete.
   *
   * Side Effects:
   * - Updates component state: `setMasterProducts` and `setFilteredProducts`.
   * - Displays toast notifications on error.
   *
   * Dependencies:
   * - Axios
   * - ApiEndpoints
   * - OrderCreationMasterData
   * - showToastMessage (for error feedback)
   */
  const fetchMasterProducts = async (): Promise<void> => {
    try {
      const response = await Axios.post(ApiEndpoints.FETCH_SPECIES_MASTERDATA, {
        masterDataType: OrderCreationMasterData.PRODUCT_MASTER_DATA,
      });
      setMasterProducts(response.data.data.masterData);
      setFilteredProducts(response.data.data.masterData);
    } catch (error) {
      showToastMessage(errorMessage.FETCH_ERROR, 'error');
    }
  };

  /**
   * useEffect hook to initialize customer ID from localStorage on component mount.
   *
   * - Retrieves the `customerDetails` object stored in localStorage.
   * - If found, parses the object and updates the `customerId` state.
   * - If not found, redirects the user to the customer list page.
   *
   * Dependencies: []
   * This effect runs only once on initial render.
   */
  useEffect(() => {
    const customerID = CustomerOrderService.getCustomerId();
    const customerDetails = CustomerOrderService.getCustomerDetails();
    setCustomerDetails(customerDetails);
    if (customerID > 0) {
      setCustomerId(customerID);
    } else {
      navigate(FrontendRoutes.CUSTOMER_LIST);
    }
  }, []);

  const fetchMasterPackage = async (): Promise<void> => {
    try {
      const response = await Axios.post(ApiEndpoints.FETCH_SPECIES_MASTERDATA, {
        masterDataType: OrderCreationMasterData.PACKAGE_MASTER_DATA,
      });
      const masterData = response.data.data.masterData.packageType;
      setMasterPackageExtenss(masterData);
    } catch (error) {
      showToastMessage(errorMessage.FETCH_ERROR, 'error');
    }
  };

  /**
   * Fetches add-on items from the server based on a search key.
   *
   * Sends a POST request to the `FETCH_ADDONS` API endpoint with the provided `searchKey`.
   * On success:
   * - Updates the `addons` state with the fetched add-on list.
   * On failure:
   * - Displays an error toast message.
   * - Rethrows the error for potential upstream error handling.
   *
   * @param {string} searchKey - The string used to filter or search add-ons on the server.
   * @returns {Promise<void>} A promise that resolves when the fetch and state update are complete.
   *
   * Dependencies:
   * - Axios for HTTP requests.
   * - ApiEndpoints.FETCH_ADDONS
   * - showToastMessage for user feedback.
   */
  const fetchAddons = async (): Promise<void> => {
    try {
      const response = await Axios.post(ApiEndpoints.FETCH_ADDONS, {});
      // Keep only add-ons that are available and not soft-deleted.
      const availableAddons = response.data.data.addOns.filter(
        (addon: Addon & { deletedAt?: string | null }) =>
          addon.availableFlag === 1 && addon.deletedAt == null,
      );
      setAddons(availableAddons);
      setFilteredAddons(availableAddons);
    } catch (error) {
      showToastMessage(errorMessage.FETCH_ERROR, 'error');
    }
  };

  /**
   * useEffect hook to retrieve order data when a valid customer ID is available.
   *
   * - This effect is triggered whenever the `customerId` state changes.
   * - If a valid `customerId` exists, it calls `getOrderData()` to fetch the associated order
   *   from localStorage and update the `orderData` state accordingly.
   *
   * Dependencies: [customerId]
   */
  useEffect(() => {
    if (customerId) {
      getOrderData();
    }
  }, [customerId]);

  /**
   * Retrieves the order data for the current customer from localStorage and updates the relevant state.
   *
   * - Fetches the `orderData` object from localStorage.
   * - Parses the data and extracts the order specific to the current `customerId`.
   * - Sets both `quantity` and `orderData` state values using the retrieved data.
   */
  const getOrderData = (): void => {
    const storageKey = isEditOrder ? 'editOrderData' : 'orderData';
    const raw = localStorage.getItem(storageKey);
    if (!raw) return;

    const parsedOrderData = JSON.parse(raw);
    if (parsedOrderData && customerId) {
      const customerOrder = parsedOrderData[customerId];
      setOrderData(customerOrder);
      const selected: Record<number, boolean> = {};
      const quantities: Record<number, number> = {};
      const existingData = customerOrder?.species?.[currentCustomizingId];
      if (existingData?.products && Array.isArray(existingData.products)) {
        existingData.products.forEach((product: ProductEntry) => {
          if (product.productId != null) {
            selected[product.productId] = true;
            quantities[product.productId] = product.quantity ?? 1;
          }
        });
      }
    }
  };

  /**
   * Synchronizes species-specific requirement flags based on the current order context.
   * * This hook manages the logic for "Required Field" flags (CITES, Permits, Licenses, etc.)
   * by checking the `speciesId` against master data.
   * * - In **Edit Mode**: It performs an asynchronous fetch to get the latest species
   * definitions, resets quantities, and updates the local cache before setting flags.
   * - In **Standard Mode**: It attempts to read and parse the existing `speciesData`
   * from localStorage to determine which flags should be enabled.
   * * @listens orderData - Re-runs when the order details or species ID changes.
   * @listens isEditOrder - Re-runs when switching between create and edit modes.
   * * @remarks
   * - Maps numeric API flags (0 or 1) to boolean values for UI logic.
   * - Updates the `requireFlag` state to drive conditional form validation and visibility.
   */
  useEffect(() => {
    const applyRequiredFlags = (matched: Species | MasterSpeciesProps) => {
      const citesFlagValue =
        'requireCitesFlag' in matched ? matched.requireCitesFlag : 0;
      const permitFlagValue =
        'requirePermitFlag' in matched
          ? matched.requirePermitFlag
          : 'requiredPermitFlag' in matched
            ? matched.requiredPermitFlag
            : 0;
      const licenseFlagValue =
        'requireLicenseFlag' in matched ? matched.requireLicenseFlag : 0;
      const lengthFlagValue =
        'requireLengthFlag' in matched ? matched.requireLengthFlag : 0;

      const nextFlags = {
        citesFlag: citesFlagValue === 1,
        PermitFlag: permitFlagValue === 1,
        LicenseFlag: licenseFlagValue === 1,
        LengthFlag: lengthFlagValue === 1,
      };

      setRequireFlag(nextFlags);

      // Keep check-in open on initial load when any check-in input is mandatory.
      if (
        nextFlags.citesFlag ||
        nextFlags.PermitFlag ||
        nextFlags.LicenseFlag ||
        nextFlags.LengthFlag
      ) {
        setCheckinOpen(true);
      }
    };

    const fetchButcherSpecies = async () => {
      try {
        const response = await Axios.post(
          ApiEndpoints.FETCH_SPECIES_MASTERDATA,
          {
            masterDataType: OrderCreationMasterData.SPECIES_MASTER_DATA,
          },
        );

        const masterData: MasterSpeciesProps[] = response.data.data.masterData;

        masterData.forEach((species: MasterSpeciesProps) => {
          species.quantity = 0;
        });

        localStorage.setItem('speciesData', JSON.stringify(masterData));

        // Set flags using fresh data after fetch
        if (orderData?.speciesId !== undefined && orderData?.speciesId !== 0) {
          const matched = masterData.find((s) => s.id === orderData.speciesId);
          if (matched) {
            applyRequiredFlags(matched);
          }
        }
      } catch (error) {
        showToastMessage(errorMessage.FETCH_ERROR, 'error');
      }
    };

    if (isEditOrder) {
      // Only fetch species data for edit orders
      fetchButcherSpecies();
    } else {
      // For new orders, read flags directly from localStorage
      if (orderData?.speciesId !== undefined && orderData?.speciesId !== 0) {
        const speciesData = localStorage.getItem('speciesData');
        const matched = JSON.parse(speciesData ?? '[]').find(
          (s: Species) => s.id === orderData.speciesId,
        );
        if (matched) {
          applyRequiredFlags(matched);
        }
      }
    }
  }, [orderData, isEditOrder]);

  /**
   * Validates a specific form field based on its name and value.
   *
   * - Checks if required fields are non-empty.
   * - Applies conditional validation for `permitNumber` and `citesNumber` based on requirement flags.
   * - Ensures `stateId` is not empty or zero.
   * - Validates `notes` length does not exceed 200 characters.
   * - Updates the error state for the given field.
   *
   * @function validateField
   * @param {string} field - The name of the form field to validate.
   * @param {string} value - The current value of the form field.
   * @returns {void}
   */
  const validateField = (field: string, value: string): void => {
    let error = '';
    switch (field) {
      case 'feet':
        if (
          requireFlag.LengthFlag &&
          (!value.trim() || parseInt(value || '0') === 0)
        ) {
          error = errorMessage.FIELD_REQUIRED;
        }
        break;
      case 'inches':
        if (
          requireFlag.LengthFlag &&
          (!value.trim() || parseInt(value || '0') === 0)
        ) {
          error = errorMessage.FIELD_REQUIRED;
        } else if (Number(value) > 11) {
          error = 'Inches cannot be greater than 11';
        }
        break;
      case 'weight':
        if (!value.trim()) {
          error = errorMessage.FIELD_REQUIRED;
        }
        break;
      case 'huntingLicenceId':
        if (
          requireFlag.LicenseFlag &&
          (!value.trim() || parseInt(value) === 0)
        ) {
          error = errorMessage.FIELD_REQUIRED;
        }
        break;
      case 'licenseNumber':
        if (requireFlag.LicenseFlag && !value.trim()) {
          error = errorMessage.FIELD_REQUIRED;
        }
        break;
      case 'permitNumber':
        if (requireFlag.PermitFlag && !value.trim()) {
          error = errorMessage.FIELD_REQUIRED;
        }
        break;
      case 'conformationNumber':
        if (requireFlag.PermitFlag && !value.trim()) {
          error = errorMessage.FIELD_REQUIRED;
        }
        break;
      case 'citesNumber':
        if (requireFlag.citesFlag && !value.trim()) {
          error = errorMessage.FIELD_REQUIRED;
        }
        break;
      case 'notes':
        if (value.trim() && value.length > 200) {
          error = errorMessage.NOTES_EXEEDS_200_CHARECTER;
        }
        break;
    }
    setErrors((prevErrors) => ({
      ...prevErrors,
      [field]: error,
    }));
  };

  /**
   * Handles changes to form inputs and updates the state.
   *
   * - Updates the `checkinOrderData` state with the new value for the specified field.
   * - If the `stateId` field is changed and requires a license, it triggers the license verification process.
   * - Validates the field value immediately after updating the state.
   *
   * @async
   * @function handleInputChange
   * @param {React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>} e - The change event triggered by the form input.
   * @returns {Promise<void>}
   */
  const handleInputChange = async (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ): Promise<void> => {
    const { name, value } = e.target;

    // Handle feet and inches with validation hooks
    if (name === 'feet') {
      feetValidation.handleChange(e as React.ChangeEvent<HTMLInputElement>);
      setCheckinOrderData((prevData) => {
        if (!prevData) return prevData;
        return {
          ...prevData,
          [name]: value === '' ? 0 : Number(value),
        };
      });
      validateField(name, value);
      return;
    }

    if (name === 'inches') {
      inchesValidation.handleChange(e as React.ChangeEvent<HTMLInputElement>);
      setCheckinOrderData((prevData) => {
        if (!prevData) return prevData;
        return {
          ...prevData,
          [name]: value === '' ? 0 : Number(value),
        };
      });
      validateField(name, value);
      return;
    }

    const fieldsWithNumbers = ['huntingLicenceId'];
    const finalValue = fieldsWithNumbers.includes(name) ? Number(value) : value;

    setCheckinOrderData((prevData) => {
      if (!prevData) return prevData;
      return {
        ...prevData,
        [name]: finalValue,
      };
    });

    validateField(name, value);
  };

  const handleLicenseSelection = (license: any) => {
    setSelectedLicenseId((prev) =>
      prev === license.id ? null : (license.id ?? null),
    );
    setCheckinOrderData((prevData) => {
      if (!prevData) return prevData;
      return {
        ...prevData,
        huntingLicenceId: license.id,
        licenseNumber: license.licenseNumber,
        harvestStateId: license.harvestStateId,
      };
    });
  };

  // Service
  const fetchServiceMaster = async (): Promise<void> => {
    try {
      const response = await Axios.post(ApiEndpoints.FETCH_SPECIES_MASTERDATA, {
        speciesId: orderData.speciesId,
        masterDataType: OrderCreationMasterData.SERVICE_MASTER_DATA,
      });
      setServices(response.data.data.masterData.services);
      setFilteredServices(response.data.data.masterData.services);
      setColors(response.data.data.masterData.colors);
    } catch (error) {
      showToastMessage(errorMessage.FETCH_ERROR, 'error');
    }
  };

  const handleColorSelect = (serviceId: number, colorId: number) => {
    setSelectedColors((prevSelectedColors) => ({
      ...prevSelectedColors,
      [serviceId]: colorId,
    }));
    setSelectedServiceIds((prevSelectedServiceIds) =>
      prevSelectedServiceIds.includes(serviceId)
        ? prevSelectedServiceIds
        : [...prevSelectedServiceIds, serviceId],
    );
    setOpenColorServiceId(null);
  };

  const toggleServiceSelection = (serviceId: number) => {
    if (isEditOrder) {
      setSelectedServiceIds((prev) =>
        prev.includes(serviceId) ? prev : [...prev, serviceId],
      );
    } else {
      setSelectedServiceIds((prev) =>
        prev.includes(serviceId)
          ? prev.filter((id) => id !== serviceId)
          : [...prev, serviceId],
      );
    }
  };

  const handleServiceSearch = (value: string) => {
    const trimmedValue = value.trim();
    setServiceSearch(value);

    const filtered = services.filter((service) =>
      service.name.toLowerCase().includes(trimmedValue.toLowerCase()),
    );

    setFilteredServices(filtered);
  };

  /**
   * Handles the "Add" action for a product.
   *
   * Opens the add-on selection sheet and sets the current product details
   * based on the provided `product` information.
   *
   * @param {ProductDetails} product - The product to be added, used to populate the customization sheet.
   * @returns {void}
   *
   */
  const handleSelectProduct = (product: ProductDetails): void => {
    setCurrentProductDetail(product);
    const defaultPack = MasterPackageExtenss.filter(
      (p) => p.defaultFlag === 1,
    ).map((p) => ({
      ...p,
      name: p.packageType,
      percentage: '100',
    }));
    setProductDraft({
      productId: product.id,
      name: product.name,
      price: product.price,
      fatPercentage: '',
      quantity:
        product.productType === ProductType.QUANTITY
          ? product.minQuantity
          : product.weight,
      instruction: '',
      addons: [],
      packages: defaultPack,
      notes: '',
      allowFatContentFlag: product.allowFatContentFlag,
      allowAddOnFlag: product.allowAddOnFlag,
      upchargeIngredientsFlag: product.upchargeIngredientsFlag,
    });
  };

  const toggleAddon = (addon: AddonsList) => {
    const isAddonUpchargeEnabled =
      Number(currentProductDetail?.upchargeIngredientsFlag ?? 0) === 1;

    setProductDraft((prev: ProductDraft | null) => {
      if (!prev) return null;

      const exists = prev.addons.find((a: AddonsList) => a.id === addon.id);

      if (exists) {
        return {
          ...prev,
          addons: prev.addons.filter((a: AddonsList) => a.id !== addon.id),
        };
      }

      const normalizedAddon: AddonsList = {
        ...addon,
        price: isAddonUpchargeEnabled ? addon.price : 0,
      };

      return {
        ...prev,
        addons: [...prev.addons, normalizedAddon],
      };
    });
  };

  const handleAddedProducts = (currentProductDetail: ProductDetails) => {
    if (percentageError) {
      return;
    }

    const newErrors: { [key: string]: string } = {};
    const draftQuantity = productDraft?.quantity;
    const hasDraftQuantity = draftQuantity !== '' && draftQuantity !== undefined;

    // Fat percentage field required validation
    if (currentProductDetail?.allowFatContentFlag === 1) {
      if (!productDraft?.fatPercentage || productDraft?.fatPercentage === '') {
        newErrors.fatPercentage = errorMessage.FIELD_REQUIRED;
      }
    }
    // Product type minimum quantity validation
    if (
      currentProductDetail.productType === ProductType.QUANTITY &&
      hasDraftQuantity &&
      Number(draftQuantity) < Number(currentProductDetail.minQuantity)
    ) {
      newErrors.quantity = `Entered value should be at least ${currentProductDetail.minQuantity}`;
    }
    // Product quantity field required validation
    if (
      currentProductDetail.productType === ProductType.QUANTITY &&
      (draftQuantity === '' || Number(draftQuantity) <= 0)
    ) {
      newErrors.quantity = errorMessage.FIELD_REQUIRED;
    }

    // Weight can be zero, but the field itself must still be filled in.
    if (
      currentProductDetail.productType === ProductType.WEIGHT &&
      draftQuantity === ''
    ) {
      newErrors.quantity = errorMessage.FIELD_REQUIRED;
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});
    setAddedProducts((prev: ProductDraft[]) => [
      ...prev,
      productDraft as ProductDraft,
    ]);
    const defaultPack = MasterPackageExtenss.filter(
      (p) => p.defaultFlag === 1,
    ).map((p) => ({
      ...p,
      name: p.packageType,
      percentage: '100',
    }));
    showToastMessage(`${productDraft?.name} added to cart`, 'success');

    setProductDraft(null);

    setSelectedPackages(defaultPack);

    setCurrentProductDetail(null);
  };

  const showRepeatButton = orderData?.quantity > 1 && currentCustomizingId > 0;
  const hasCompletedProductInEditOrder =
    isEditOrder &&
    addedProducts.some(
      (product) => (productStatusMap[product.productId ?? -1] ?? 0) > 0,
    );

  /**
   * Constructs a structured check-in data object based on species-specific requirements.
   * * This function dynamically builds the check-in payload by:
   * 1. Initializing core fields (weight, notes, state).
   * 2. Conditionally adding Licensing, Permit, CITES, and Dimension (Length) data if the
   * `requireFlag` criteria are met or if the data already exists.
   * 3. Sanitizing the final object by removing empty strings, nulls, and zeros (excluding
   * state-related identifiers).
   * * @returns {object} A sanitized check-in object ready for API submission or local storage.
   * * @remarks
   * - Uses `Number()` and `String()` casting to ensure data type consistency.
   * - `harvestStateId` and `stateName` are protected from the deletion logic to ensure
   * location metadata is preserved even if values are falsy.
   * - Implements a "Sparse Object" pattern where only relevant keys exist in the final output.
   */
  const buildCheckIn = () => {
    const checkIn: any = {
      weight: String(checkinOrderData?.weight ?? ''),
      notes: checkinOrderData?.notes ?? '',
      stateName: checkinOrderData?.stateName,
    };

    if (requireFlag.LicenseFlag) {
      checkIn.huntingLicenceId = Number(checkinOrderData?.huntingLicenceId);
      checkIn.licenseNumber = checkinOrderData?.licenseNumber;
      checkIn.harvestStateId = Number(checkinOrderData?.harvestStateId);
    }

    if (requireFlag.PermitFlag || checkinOrderData?.permitNumber) {
      checkIn.permitNumber = checkinOrderData?.permitNumber;
    }
    if (requireFlag.PermitFlag || checkinOrderData?.conformationNumber) {
      checkIn.conformationNumber = checkinOrderData?.conformationNumber;
    }
    if (requireFlag.citesFlag || checkinOrderData?.citesNumber) {
      checkIn.citesNumber = checkinOrderData?.citesNumber;
    }

    if (requireFlag.LengthFlag) {
      checkIn.feet = Number(checkinOrderData?.feet);
      checkIn.inches = Number(checkinOrderData?.inches);
    }

    // Delete any keys with empty, null, 0, or undefined values
    Object.keys(checkIn).forEach((key) => {
      if (key === 'harvestStateId' || key === 'stateName') return;
      const val = checkIn[key];
      if (val === undefined || val === null || val === '' || val === 0) {
        delete checkIn[key];
      }
    });

    return checkIn;
  };

  const hasCheckinData = (): boolean => {
    if (!checkinOrderData) return false;

    if (requireFlag.LicenseFlag) {
      if (
        !checkinOrderData.huntingLicenceId ||
        checkinOrderData.huntingLicenceId === 0
      )
        return false;
      if (!checkinOrderData.licenseNumber) return false;
    }

    if (requireFlag.PermitFlag) {
      if (!checkinOrderData.permitNumber) return false;
    }

    if (requireFlag.citesFlag) {
      if (!checkinOrderData.citesNumber) return false;
    }

    if (requireFlag.LengthFlag) {
      if (!checkinOrderData.feet || checkinOrderData.feet === 0) return false;
      if (!checkinOrderData.inches || checkinOrderData.inches === 0)
        return false;
      if (Number(checkinOrderData.inches) > 11) return false;
    }

    return true;
  };

  const hasUnsavedChanges = (): boolean => {
    return (
      selectedServiceIds.length > 0 ||
      addedProducts.length > 0 ||
      hasCheckinData()
    );
  };

  const validateCurrentSpeciesData = (): boolean => {
    // A species must include at least one service or one product.
    if (addedProducts.length === 0 && selectedServiceIds.length === 0) {
      showConfirmationToast(
        'A service or a product must be selected to proceed. Please choose at least one to continue.',
        'Service or Product Required',
        'info',
        () => {
          setServiceOpen(true);
          setProductOpen(true);
        },
        'Select Service or Product',
        () => { },
        'Cancel',
      );
      return false;
    }

    for (const serviceId of selectedServiceIds) {
      const serviceInfo = filteredServices.find((s) => s.id === serviceId);
      const selectedColor = selectedColors[serviceId];
      if (
        serviceInfo?.colors === 1 &&
        (selectedColor === undefined || selectedColor === null)
      ) {
        showToastMessage(errorMessage.SERVICE_COLOR_REQUIRED, 'error');
        return false;
      }
    }

    // Checkin validation
    if (!hasCheckinData()) {
      showToastMessage(errorMessage.CHECKIN_REQUIRED, 'error');
      return false;
    }

    return true;
  };

  const handleSaveOrNextValidation = () => {
    if (!validateCurrentSpeciesData()) return;

    if (!hasSpeciesDataChangedSinceLastSave()) {
      handleSaveOrNext();
      return;
    }

    // Final confirmation
    showConfirmationToast(
      `Would you like to save changes for ${orderData?.speciesName} - ${currentCustomizingId + 1}?`,
      'Save Changes',
      'info',
      handleSaveOrNext, // Save
      'Save',
      () => { }, // cancel action
      'Cancel',
    );
  };

  const handleSaveCurrentSpeciesValidation = () => {
    if (!validateCurrentSpeciesData()) return;

    showConfirmationToast(
      `Would you like to save changes for ${orderData?.speciesName} - ${currentCustomizingId + 1}?`,
      'Save Changes',
      'info',
      handleSaveCurrentSpecies,
      'Save',
      () => { },
      'Cancel',
    );
  };

  /**
   * Determines whether a species has been modified by comparing
   * its original state with the current state.
   *
   * @param original - Original species data (baseline)
   * @param current - Current species data (after user modifications)
   *
   * @returns boolean
   * - true  → species has changed
   * - false → no changes detected
   *
   */
  const hasSpeciesChanged = (original: any, current: any): boolean => {
    // Compare services
    const originalServiceIds = original.services
      ?.map((service: { serviceId: number }) => service.serviceId)
      .sort();
    const currentServiceIds = current.services
      ?.map((service: { serviceId: number }) => service.serviceId)
      .sort();
    if (
      JSON.stringify(originalServiceIds) !== JSON.stringify(currentServiceIds)
    )
      return true;

    // Compare deleted arrays
    if (current.deletedServiceIds?.length > 0) return true;
    if (current.deletedProductIds?.length > 0) return true;

    // Compare products
    if (original.products?.length !== current.products?.length) return true;
    const originalProducts = JSON.stringify(
      original.products?.map(
        (product: {
          productId: number;
          quantity: number;
          fatPercent: number;
          addOnIds: number[];
        }) => ({
          productId: product.productId,
          quantity: product.quantity,
          fatPercent: product.fatPercent,
          addOnIds: [...(product.addOnIds ?? [])].sort(),
        }),
      ),
    );

    const currentProducts = JSON.stringify(
      current.products?.map(
        (product: {
          productId: number;
          quantity: number;
          fatPercent: number;
          addOnIds: number[];
        }) => ({
          productId: product.productId,
          quantity: product.quantity,
          fatPercent: product.fatPercent,
          addOnIds: [...(product.addOnIds ?? [])].sort(),
        }),
      ),
    );

    if (originalProducts !== currentProducts) return true;

    return false;
  };

  const buildCurrentSpeciesData = () => {
    const services = selectedServiceIds.map((serviceId) => {
      if (isEditOrder && editOrderData) {
        const existingSpecies =
          editOrderData[customerId]?.species?.[currentCustomizingId];
        const existingService = existingSpecies?.services?.find(
          (service: { serviceId: number; butcherServiceId: number }) =>
            service.serviceId === serviceId ||
            service.butcherServiceId === serviceId,
        );

        if (!existingService) {
          return {
            serviceId: null,
            status: null,
            butcherServiceId: serviceId,
            ...(selectedColors[serviceId]
              ? { colorId: selectedColors[serviceId] }
              : {}),
            depositAmount: 0,
          };
        }
        return {
          serviceId: existingService.serviceId ?? null,
          status: existingService.status ?? null,
          butcherServiceId: existingService.butcherServiceId ?? serviceId,
          ...(selectedColors[serviceId]
            ? { colorId: selectedColors[serviceId] }
            : {}),
          depositAmount: existingService.depositAmount ?? 0,
        };
      }

      const service: { serviceId: number; colorId?: number } = { serviceId };
      if (selectedColors[serviceId]) service.colorId = selectedColors[serviceId];
      return service;
    });

    const products = addedProducts?.map((product: ProductDraft) => ({
      productId: product?.productId ?? null,
      butcherProductId: product.butcherProductId,
      status: product.status ?? null,
      quantity: Number(product.quantity),
      fatPercent: Number(product.fatPercentage),
      addOnIds: product.addons?.map((addon: AddonsList) => addon.id),
      packaging: product.packages?.map((pkg: OrderPackage) => ({
        productPackageTypeId: pkg.id,
        percentage: normalizePackagePercentageNumber(pkg.percentage),
      })),
      packagingNotes: product.notes,
    }));

    return {
      speciesName: `${orderData?.speciesName} - ${currentCustomizingId + 1}`,
      speciesId: isEditOrder
        ? orderData.species[currentCustomizingId]?.speciesId // edit order - butcherSpeciesId
        : orderData.speciesId, // new order - speciesId
      checkIn: buildCheckIn(),
      services,
      products,
      additionalNotes: '',
    };
  };

  const normalizeSpeciesForCompare = (
    species:
      | {
        speciesName?: string;
        speciesId?: number;
        checkIn?: Record<string, unknown>;
        services?: Array<Record<string, unknown>>;
        products?: Array<Record<string, unknown>>;
        additionalNotes?: string;
      }
      | undefined,
  ) => {
    const normalizedCheckIn = {
      weight: Number(species?.checkIn?.weight ?? 0),
      notes: String(species?.checkIn?.notes ?? ''),
      stateName: String(species?.checkIn?.stateName ?? ''),
      feet: Number(species?.checkIn?.feet ?? 0),
      inches: Number(species?.checkIn?.inches ?? 0),
      huntingLicenceId: Number(species?.checkIn?.huntingLicenceId ?? 0),
      licenseNumber: String(species?.checkIn?.licenseNumber ?? ''),
      harvestStateId: Number(species?.checkIn?.harvestStateId ?? 0),
      permitNumber: String(species?.checkIn?.permitNumber ?? ''),
      conformationNumber: String(species?.checkIn?.conformationNumber ?? ''),
      citesNumber: String(species?.checkIn?.citesNumber ?? ''),
    };

    const normalizedServices = (species?.services ?? [])
      .map((service) => ({
        serviceId: Number(service.serviceId ?? service.butcherServiceId ?? 0),
        colorId: Number(service.colorId ?? 0),
      }))
      .sort((a, b) => a.serviceId - b.serviceId || a.colorId - b.colorId);

    const normalizedProducts = (species?.products ?? [])
      .map((product) => {
        const normalizedPackaging = (
          (product.packaging as Array<Record<string, unknown>> | undefined) ?? []
        )
          .map((pkg) => ({
            productPackageTypeId: Number(
              pkg.productPackageTypeId ?? pkg.id ?? 0,
            ),
            percentage: normalizePackagePercentageNumber(pkg.percentage ?? 0),
          }))
          .sort(
            (a, b) =>
              a.productPackageTypeId - b.productPackageTypeId ||
              a.percentage - b.percentage,
          );

        const normalizedAddOns = (
          (product.addOnIds as Array<number | string> | undefined) ?? []
        )
          .map((id) => Number(id))
          .sort((a, b) => a - b);

        return {
          productId: Number(product.productId ?? product.butcherProductId ?? 0),
          quantity: Number(product.quantity ?? 0),
          fatPercent: Number(product.fatPercent ?? product.fatPercentage ?? 0),
          addOnIds: normalizedAddOns,
          packaging: normalizedPackaging,
          packagingNotes: String(product.packagingNotes ?? product.notes ?? ''),
        };
      })
      .sort(
        (a, b) =>
          a.productId - b.productId ||
          a.quantity - b.quantity ||
          a.fatPercent - b.fatPercent ||
          a.packagingNotes.localeCompare(b.packagingNotes),
      );

    return {
      speciesName: String(species?.speciesName ?? ''),
      speciesId: Number(species?.speciesId ?? 0),
      checkIn: normalizedCheckIn,
      services: normalizedServices,
      products: normalizedProducts,
      additionalNotes: String(species?.additionalNotes ?? ''),
    };
  };

  const hasSpeciesDataChangedSinceLastSave = (): boolean => {
    const storageKey = isEditOrder ? 'editOrderData' : 'orderData';
    const raw = localStorage.getItem(storageKey);
    if (!raw) return true;

    const all = JSON.parse(raw);
    const savedSpecies = all[customerId]?.species?.[currentCustomizingId];
    if (!savedSpecies) return true;

    const currentSpeciesData = buildCurrentSpeciesData();
    const normalizedSaved = normalizeSpeciesForCompare(savedSpecies);
    const normalizedCurrent = normalizeSpeciesForCompare(currentSpeciesData);

    return JSON.stringify(normalizedSaved) !== JSON.stringify(normalizedCurrent);
  };

  const persistCurrentSpecies = () => {
    const currentSpeciesData = buildCurrentSpeciesData();

    // isEditOrder — save to editOrderData
    if (isEditOrder) {
      const raw = localStorage.getItem('editOrderData');
      if (!raw) return;
      const all = JSON.parse(raw);
      const existingSpecies = all[customerId].species[currentCustomizingId];

      const isEdited = hasSpeciesChanged(existingSpecies, currentSpeciesData);

      all[customerId].species[currentCustomizingId] = {
        ...currentSpeciesData,
        deletedServiceIds: existingSpecies?.deletedServiceIds ?? [],
        deletedProductIds: existingSpecies?.deletedProductIds ?? [],
        creditAmount: existingSpecies?.creditAmount ?? 0,
        isEdited,
      };

      localStorage.setItem('editOrderData', JSON.stringify(all));
    } else {
      // Save to orderData
      const existing = localStorage.getItem('orderData');
      const allCustomizations = existing ? JSON.parse(existing) : {};
      const customerData = allCustomizations[customerId];
      if (!customerData.species) {
        customerData.species = [];
      }
      customerData.species[currentCustomizingId] = currentSpeciesData;
      allCustomizations[customerId] = customerData;
      localStorage.setItem('orderData', JSON.stringify(allCustomizations));
    }
  };

  const handleSaveOrNext = () => {
    persistCurrentSpecies();

    // Navigate to next or submit
    if (currentCustomizingId < orderData?.quantity - 1) {
      const nextId = currentCustomizingId + 1;
      localStorage.setItem('currentCustomizingId', String(nextId));
      setCurrentCustomizingId(nextId);
      navigate(0);
    } else {
      if (isEditOrder) {
        navigate('/create-order/cart', {
          state: {
            from: { orderDetails: true },
            orderId: orderId,
          },
        });
      } else {
        navigate('/create-order/cart');
      }
    }
  };

  const handleSaveCurrentSpecies = () => {
    persistCurrentSpecies();
    window.dispatchEvent(new Event('order-data-updated'));
    showToastMessage('Current species saved successfully.', 'success');
  };

  const handlePrevSet = () => {
    if (currentCustomizingId <= 0) return;
    const newIndex = currentCustomizingId - 1;

    if (hasUnsavedChanges()) {
      showConfirmationToast(
        'You have unsaved changes. Are you sure you want to leave without saving?',
        'Unsaved Changes',
        'info',
        () => { }, // stay
        'Stay on This Tab',
        () => {
          // Leave without saving
          localStorage.setItem('currentCustomizingId', String(newIndex));
          getOrderData();
          navigate(0);
        },
        'Leave Without Saving',
      );
      return;
    }

    localStorage.setItem('currentCustomizingId', String(newIndex));
    getOrderData();
  };

  const handleNextSet = () => {
    const totalSets = orderData.quantity;
    if (currentCustomizingId >= totalSets - 1) return;
    const newIndex = currentCustomizingId + 1;

    if (hasUnsavedChanges()) {
      showConfirmationToast(
        'You have unsaved changes. Are you sure you want to leave without saving?',
        'Unsaved Changes',
        'info',
        () => { },
        'Stay on This Tab',
        () => {
          // Leave without saving
          localStorage.setItem('currentCustomizingId', String(newIndex));
          getOrderData();
          navigate(0);
        },
        'Leave Without Saving',
      );
      return;
    }

    localStorage.setItem('currentCustomizingId', String(newIndex));
    getOrderData();
  };

  const handleAddNewProductClose = () => {
    setAddNewProduct(false);
  };

  /**
   * Resolves the correct service identifier based on the current order context.
   * * In "Edit Mode," this function maps a generic `serviceId` to its original
   * database counterpart or its butcher-specific ID by searching the local
   * order cache. If not in edit mode, or if no match is found, it returns
   * the original ID provided.
   *
   * @param {number} serviceId - The ID to be mapped (could be a Butcher ID or a Temp ID).
   * * @returns {number} The resolved ID, prioritized by:
   * 1. `originalServiceId` (from DB)
   * 2. `serviceId` (from local draft)
   * 3. The input `serviceId` (fallback)
   *
   * @remarks
   * - Essential for operations like `getServiceDeposit` where a specific
   * database primary key might be required for the lookup.
   * - Safeguards against data mismatches when services are added/removed
   * during an edit session.
   */
  const getMappedServiceId = (serviceId: number): number => {
    if (!isEditOrder) return serviceId;

    const raw = localStorage.getItem('editOrderData');
    if (!raw) return serviceId;

    const all = JSON.parse(raw);
    const existingSpecies = all[customerId]?.species?.[currentCustomizingId];

    const existingService = existingSpecies?.services?.find(
      (service: { butcherServiceId: number; serviceId: number }) =>
        service.butcherServiceId === serviceId ||
        service.serviceId === serviceId,
    );

    return (
      existingService?.originalServiceId ??
      existingService?.serviceId ??
      serviceId
    );
  };

  return (
    <div>
      <div className="p-3 pb-2">
        {/* License modal */}
        {showLicenseForm && (
          <AddOrEditLicense
            onClose={() => {
              setShowLicenseForm(false);
              refreshCustomerDetails();
              setEditingLicense(null);
            }}
            licenseToEdit={editingLicense}
          />
        )}
        {addNewProduct && (
          <AddNewProductModal
            onClose={() => {
              showConfirmationToast(
                'Are you sure you want to Discard product?',
                'Discard product',
                'delete',
                handleAddNewProductClose,
                'Discard product', // button name
                () => { },
                'Cancel',
              );
            }}
            onAdd={(product) => {
              showToastMessage(`${product.name} added to cart.`, 'success');
              handleDuplicateProduct(product, currentCustomizingId);
              setAddNewProduct(false);
            }}
            masterProducts={masterProducts}
            addons={addons}
            masterPackages={MasterPackageExtenss}
            addedProducts={addedProducts}
          />
        )}

        <div className="flex items-center justify-between">
          {/* LEFT SIDE — arrows + title */}
          <div className="flex items-center">
            {/* Left Arrow */}
            <div
              className={`w-8 h-8 flex items-center justify-center rounded-full mr-3 cursor-pointer
        ${currentCustomizingId <= 0 ? 'bg-[#B4B4B4] text-white opacity-40 cursor-not-allowed' : 'bg-primary text-white'}`}
              onClick={handlePrevSet}
            >
              <svg
                width="13"
                height="13"
                viewBox="0 0 13 13"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M6.26465 12.4424L0.264648 6.44238L0.176758 6.35352L0.264648 6.26465L6.26465 0.264648L6.35352 0.176758L6.44238 0.264648L7.50488 1.32715L7.59277 1.41602L7.50488 1.50488L3.53125 5.47852L12.4785 5.47852V7.22852L3.53125 7.22852L7.50488 11.2021L7.59277 11.291L7.50488 11.3799L6.44238 12.4424L6.35352 12.5303L6.26465 12.4424Z"
                  fill="white"
                  stroke="white"
                  strokeWidth="0.25"
                />
              </svg>
            </div>

            <h1 className="font-semibold text-xl">
              {orderData?.speciesName} - {currentCustomizingId + 1}
            </h1>

            {/* Right Arrow */}
            <div
              className={`w-8 h-8 flex items-center justify-center rounded-full ml-3 cursor-pointer
                ${currentCustomizingId >= (orderData?.quantity ?? 0) - 1
                  ? 'bg-gray-300 opacity-40 cursor-not-allowed'
                  : 'bg-primary'
                }`}
              onClick={handleNextSet}
            >
              <svg
                width="13"
                height="13"
                viewBox="0 0 13 13"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M6.33887 0.264648L12.3389 6.26465L12.4268 6.35352L12.3389 6.44238L6.33887 12.4424L6.25 12.5303L6.16113 12.4424L5.09863 11.3799L5.01074 11.291L5.09863 11.2021L9.07227 7.22852H0.125V5.47852H9.07227L5.09863 1.50488L5.01074 1.41602L5.09863 1.32715L6.16113 0.264648L6.25 0.176758L6.33887 0.264648Z"
                  fill="white"
                  stroke="white"
                  strokeWidth="0.25"
                />
              </svg>
            </div>
          </div>

          {/* Next species button */}
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handleSaveCurrentSpeciesValidation}
              className="flex items-center gap-2 px-6 py-2 lg:w-[160px] md:w-[140px] xl:w-[180px] justify-center border rounded-md font-semibold text-primary border-primary bg-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
            >
              Save
            </button>
            {currentCustomizingId < orderData?.quantity - 1 ? (
              <button
                type="button"
                onClick={handleSaveOrNextValidation}
                className="flex items-center gap-2 px-6 py-2 lg:w-[304px] md:w-[268px] xl:w-[499px] justify-center border rounded-md font-semibold text-primary border-primary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
              >
                Next {orderData?.speciesName}
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12.175 8.98657H0V6.98657H12.175L6.575 1.38657L8 -0.0134277L16 7.98657L8 15.9866L6.575 14.5866L12.175 8.98657Z"
                    fill="#004834"
                  />
                </svg>
              </button>
            ) : (
              <>
                {/* Go to Cart button */}
                <button
                  type="button"
                  onClick={handleSaveOrNextValidation}
                  className="flex items-center gap-2 px-6 py-2 lg:w-[304px] md:w-[268px] xl:w-[499px] justify-center border rounded-md font-semibold bg-primary text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                >
                  Go to Cart
                </button>
              </>
            )}
          </div>
        </div>
      </div>
      <div className="p-3 grid grid-cols-5 gap-2 w-full">
        <div className="col-span-3 bg-white p-2 rounded-sm">
          {!isEditOrder && showRepeatButton && (
            <div className="relative inline-block">
              <button
                onClick={() => setOpenRepeatSpecies(!openRepeatSpecies)}
                type="button"
                className="flex items-center gap-2 px-6 py-2 mb-2 border border-primary rounded-md font-medium text-primary bg-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
              >
                Same as {orderData?.speciesName} - 1
                <svg
                  width="14"
                  height="8"
                  viewBox="0 0 14 8"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M1.25 1.25L6.75 6.25L12.25 1.25"
                    stroke="#004834"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>

              {openRepeatSpecies && (
                <div
                  className="absolute left-0 top-full mt-2 w-48 bg-white border rounded-lg shadow-lg z-10"
                  ref={(el) => {
                    if (el) {
                      dropdownRefs.current.set(1, el);
                    } else {
                      dropdownRefs.current.delete(1);
                    }
                  }}
                >
                  <ul className="py-2 text-sm">
                    {otherSpecies.map((sp) => (
                      <li
                        key={sp.index}
                        onClick={() => handleSameAs(sp.index)}
                        className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                      >
                        Same as {sp.label}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Accordion Check-in */}
          <div className="rounded-md shadow-sm mb-3">
            <button
              onClick={() => setCheckinOpen(!checkinOpen)}
              type="button"
              className="w-full flex justify-between items-center bg-[#EAF1F866] border border-[#2A6FBA1A] p-2 font-semibold text-lg outline-none"
            >
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#DFEEFF]">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M10 20C8.61667 20 7.31667 19.7375 6.1 19.2125C4.88333 18.6875 3.825 17.975 2.925 17.075C2.025 16.175 1.3125 15.1167 0.7875 13.9C0.2625 12.6833 0 11.3833 0 10C0 8.61667 0.2625 7.31667 0.7875 6.1C1.3125 4.88333 2.025 3.825 2.925 2.925C3.825 2.025 4.88333 1.3125 6.1 0.7875C7.31667 0.2625 8.61667 0 10 0C11.3833 0 12.6833 0.2625 13.9 0.7875C15.1167 1.3125 16.175 2.025 17.075 2.925C17.975 3.825 18.6875 4.88333 19.2125 6.1C19.7375 7.31667 20 8.61667 20 10C20 11.3833 19.7375 12.6833 19.2125 13.9C18.6875 15.1167 17.975 16.175 17.075 17.075C16.175 17.975 15.1167 18.6875 13.9 19.2125C12.6833 19.7375 11.3833 20 10 20ZM10 18C12.2333 18 14.125 17.225 15.675 15.675C17.225 14.125 18 12.2333 18 10C18 7.76667 17.225 5.875 15.675 4.325C14.125 2.775 12.2333 2 10 2C7.76667 2 5.875 2.775 4.325 4.325C2.775 5.875 2 7.76667 2 10C2 12.2333 2.775 14.125 4.325 15.675C5.875 17.225 7.76667 18 10 18ZM6 14V10.925L11.525 5.425C11.675 5.275 11.8417 5.16667 12.025 5.1C12.2083 5.03333 12.3917 5 12.575 5C12.775 5 12.9667 5.0375 13.15 5.1125C13.3333 5.1875 13.5 5.3 13.65 5.45L14.575 6.375C14.7083 6.525 14.8125 6.69167 14.8875 6.875C14.9625 7.05833 15 7.24167 15 7.425C15 7.60833 14.9667 7.79583 14.9 7.9875C14.8333 8.17917 14.725 8.35 14.575 8.5L9.075 14H6ZM7.5 12.5H8.45L11.475 9.45L11.025 8.975L10.55 8.525L7.5 11.55V12.5ZM11.025 8.975L10.55 8.525L11.475 9.45L11.025 8.975Z"
                      fill="#2A6FBA"
                    />
                  </svg>
                </div>

                <span>Check - In</span>
              </div>
              <div className="flex items-center gap-4">
                <FontAwesomeIcon
                  className={`text-[#2A6FBA] transition-transform duration-300 ${checkinOpen ? '' : 'rotate-180'
                    }`}
                  icon={faChevronUp}
                />
              </div>
            </button>
            {/* Accordion Content */}
            {checkinOpen && (
              <div className="Checkin-boxes bg-white">
                {isEditOrder && (
                  <span className="mt-2 inline-flex items-center px-2 py-1 text-xs font-medium text-[#2A6FBA]">
                    Note: Checkin details cannot be edited
                  </span>
                )}
                {/* License details */}
                {requireFlag.LicenseFlag && (
                  <div
                    className={`mt-2 border border-b-gray-400 rounded-lg shadow-md bg-[#FFFEFA] ${isEditOrder ? 'opacity-50 pointer-events-none' : ''}`}
                  >
                    <div className="flex justify-between items-center mt-2 p-2">
                      <span>License details</span>
                      <button
                        onClick={() => setShowLicenseForm(true)}
                        className="text-primary underline"
                        type="button"
                      >
                        + Add New License
                      </button>
                    </div>
                    <div className="mt-4 rounded  overflow-hidden">
                      <table className="w-full text-sm">
                        <thead className="text-left bg-[#F5F5F5]">
                          <tr>
                            <th className="px-2 py-1 lg:px-4 lg:py-3 font-medium">
                              {/* Checkbox header – optional, you can add a "Select All" later if needed */}
                            </th>
                            <th className="px-2 py-1 lg:px-4 lg:py-3 font-medium">Name</th>
                            <th className="px-2 py-1 lg:px-4 lg:py-3 font-medium">State</th>
                            <th className="px-2 py-1 lg:px-4 lg:py-3 font-medium">License Number</th>
                            <th className="px-2 py-1 lg:px-4 lg:py-3 font-medium">Date</th>
                            <th className="px-2 py-1 lg:px-4 lg:py-3"></th>
                          </tr>
                        </thead>
                        <tbody className="p-1">
                          {validLicenses.map((license) => (
                            <tr
                              key={license.id}
                              onClick={() => handleLicenseSelection(license)}
                              className={`cursor-pointer transition-all
          ${selectedLicenseId === license.id
                                  ? 'bg-[#F1F8ED] border border-primary'
                                  : 'hover:bg-[#F1F8ED] hover:border hover:border-primary'
                                }`}
                            >
                              <td className="px-2 py-1 lg:px-4 lg:py-3">
                                <input
                                  type="checkbox"
                                  checked={selectedLicenseId === license.id}
                                  onChange={() => handleLicenseSelection(license)}
                                  onClick={(e) => e.stopPropagation()}
                                  className="accent-primary h-4 w-4"
                                />
                              </td>
                              <td className="px-2 py-1 lg:px-4 lg:py-3 font-medium">
                                {customerDetails?.firstName || ''} {customerDetails?.lastName || ''}
                              </td>
                              <td className="px-2 py-1 lg:px-4 lg:py-3">
                                {license.HarvestState?.stateName}
                              </td>
                              <td className="px-2 py-1 lg:px-4 lg:py-3">
                                {license.licenseNumber}
                              </td>
                              <td className="px-2 py-1 lg:px-4 lg:py-3 whitespace-nowrap">
                                {formatDate(license?.expiryDate || '')}
                              </td>
                              <td className="px-2 py-1 lg:px-4 lg:py-3">
                                <svg
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    setEditingLicense({
                                      id: license.id ?? -1,
                                      harvestStateId: license.harvestStateId ?? 0,
                                      firstName: customerDetails?.firstName || '',
                                      lastName: customerDetails?.lastName || '',
                                      licenseNumber: license.licenseNumber || '',
                                      expiryDate: license.expiryDate || '',
                                    });
                                    setShowLicenseForm(true);
                                  }}
                                  width="12"
                                  height="14"
                                  viewBox="0 0 14 14"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    d="M1.55556 12.4444H2.66389L10.2667 4.84167L9.15833 3.73333L1.55556 11.3361V12.4444ZM0 14V10.6944L10.2667 0.447222C10.4222 0.30463 10.594 0.194444 10.7819 0.116667C10.9699 0.0388889 11.1676 0 11.375 0C11.5824 0 11.7833 0.0388889 11.9778 0.116667C12.1722 0.194444 12.3407 0.311111 12.4833 0.466667L13.5528 1.55556C13.7083 1.69815 13.8218 1.86667 13.8931 2.06111C13.9644 2.25556 14 2.45 14 2.64444C14 2.85185 13.9644 3.04954 13.8931 3.2375C13.8218 3.42546 13.7083 3.59722 13.5528 3.75278L3.30556 14H0ZM9.70278 4.29722L9.15833 3.73333L10.2667 4.84167L9.70278 4.29722Z"
                                    fill="#4D545C"
                                  />
                                </svg>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
                {/* Flag details */}
                {(requireFlag.PermitFlag || requireFlag.citesFlag) && (
                  <div
                    className={`mt-2 border border-b-gray-400 p-4 rounded-lg shadow-md bg-[#FFFEFA] ${isEditOrder ? 'opacity-50 pointer-events-none' : ''}`}
                  >
                    <div className="grid grid-cols-2 sm:grid-cols-2 gap-4 mt-2">
                      {requireFlag.PermitFlag && (
                        <div>
                          <label
                            htmlFor="permitNumber"
                            className="block text-sm font-medium text-[#757575] mb-2"
                          >
                            Permit Number *
                          </label>
                          <input
                            type="text"
                            name="permitNumber"
                            disabled={isEditOrder}
                            value={checkinOrderData?.permitNumber}
                            onChange={handleInputChange}
                            required={requireFlag.PermitFlag}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm outline-gray-320 focus:outline-primary outline-gray-300"
                          />
                          {errors.permitNumber && (
                            <p className="text-red-500 text-xs mt-1">
                              {errors.permitNumber}
                            </p>
                          )}
                        </div>
                      )}
                      {requireFlag.citesFlag && (
                        <div>
                          <label
                            htmlFor="citesNumber"
                            className="block text-sm font-medium text-[#757575] mb-2"
                          >
                            CITES Number *
                          </label>
                          <input
                            type="text"
                            name="citesNumber"
                            disabled={isEditOrder}
                            value={checkinOrderData?.citesNumber}
                            onChange={handleInputChange}
                            required={requireFlag.citesFlag}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm outline-gray-320 focus:outline-primary outline-gray-300"
                          />
                          {errors.citesNumber && (
                            <p className="text-red-500 text-xs mt-1">
                              {errors.citesNumber}
                            </p>
                          )}
                        </div>
                      )}
                      {requireFlag.PermitFlag && (
                        <div>
                          <label
                            htmlFor="conformationNumber"
                            className="block text-sm font-medium text-[#757575] mb-2"
                          >
                            Confirmation Number *
                          </label>
                          <input
                            type="text"
                            name="conformationNumber"
                            disabled={isEditOrder}
                            value={checkinOrderData?.conformationNumber}
                            onChange={handleInputChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm outline-gray-320 focus:outline-primary outline-gray-300"
                          />
                          {errors.conformationNumber && (
                            <p className="text-red-500 text-xs mt-1">
                              {errors.conformationNumber}
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
                <div
                  className={`mt-2 border border-b-gray-400 p-4 rounded-lg shadow-md bg-[#FFFEFA] ${isEditOrder ? 'opacity-50 pointer-events-none' : ''}`}
                >
                  {requireFlag.LengthFlag && (
                    <div className="grid grid-cols-1 gap-4 mt-2">
                      <div>
                        <label
                          htmlFor="length"
                          className="block text-sm font-medium text-[#757575] mb-2"
                        >
                          Length *
                        </label>
                        <div className="flex gap-2">
                          {/* Feet Input */}
                          <input
                            type="text"
                            name="feet"
                            disabled={isEditOrder}
                            placeholder="0"
                            value={
                              feetValidation.value ?? checkinOrderData?.feet
                            }
                            onChange={handleInputChange}
                            onKeyDown={feetValidation.handleKeyPress}
                            className="w-1/2 px-3 py-2 border border-gray-300 rounded-md text-sm outline-gray-320 focus:outline-primary outline-gray-300"
                          />
                          <span className="flex items-center text-gray-500 text-sm">
                            Feet
                          </span>
                          <input
                            type="text"
                            disabled={isEditOrder}
                            name="inches"
                            placeholder="0"
                            value={
                              inchesValidation.value ?? checkinOrderData?.inches
                            }
                            onChange={handleInputChange}
                            onKeyDown={inchesValidation.handleKeyPress}
                            className="w-1/2 px-3 py-2 border border-gray-300 rounded-md text-sm outline-gray-320 focus:outline-primary outline-gray-300"
                          />
                          <span className="flex items-center text-gray-500 text-sm">
                            Inches
                          </span>
                        </div>
                      </div>
                      {(errors.feet ||
                        errors.inches ||
                        feetValidation.error ||
                        inchesValidation.error) && (
                          <p className="text-red-500 text-xs mt-1">
                            {errors.feet ||
                              errors.inches ||
                              feetValidation.error ||
                              inchesValidation.error}
                          </p>
                        )}
                    </div>
                  )}
                  <div className="grid grid-cols-2 sm:grid-cols-2 gap-4 mt-2">
                    <div>
                      <label
                        htmlFor="weight"
                        className="block text-sm font-medium text-[#757575] mb-2"
                      >
                        Weight
                      </label>
                      <input
                        value={checkinOrderData?.weight}
                        disabled={isEditOrder}
                        onChange={handleInputChange}
                        type="text"
                        name="weight"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm outline-gray-320 focus:outline-primary outline-gray-300"
                      />
                      {errors.weight && (
                        <p className="text-red-500 text-xs mt-1">
                          {errors.weight}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-1 mt-2">
                    <label
                      htmlFor="notes"
                      className="block text-sm font-medium text-[#757575] mb-2"
                    >
                      Notes
                    </label>
                    <textarea
                      name="notes"
                      disabled={isEditOrder}
                      value={checkinOrderData?.notes}
                      onChange={handleInputChange}
                      placeholder="Add notes or description..."
                      className=" block w-full border border-gray-300 rounded-md p-2 mt-1 text-sm focus:outline-primary"
                      rows={3}
                    />
                    {errors.notes && (
                      <p className="text-red-500 text-xs mt-1">
                        {errors.notes}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Accordion Services */}
          <div className="rounded-md shadow-sm mb-3 mt-4">
            <button
              onClick={() => setServiceOpen(!serviceOpen)}
              type="button"
              className="w-full flex justify-between items-center bg-[#F5EEFB66] border border-[#9F57D41A] p-2 font-semibold text-lg outline-none"
            >
              {/* Left */}
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#F3E8FF]">
                  <svg
                    width="20"
                    height="16"
                    viewBox="0 0 20 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M0 16V6C0 5.45 0.195833 4.97917 0.5875 4.5875C0.979167 4.19583 1.45 4 2 4H5V2C5 1.45 5.19583 0.979167 5.5875 0.5875C5.97917 0.195833 6.45 0 7 0H13C13.55 0 14.0208 0.195833 14.4125 0.5875C14.8042 0.979167 15 1.45 15 2V4H18C18.55 4 19.0208 4.19583 19.4125 4.5875C19.8042 4.97917 20 5.45 20 6V16H0ZM6 11V12H4V11H2V14H18V11H16V12H14V11H6ZM2 6V9H4V8H6V9H14V8H16V9H18V6H2ZM7 4H13V2H7V4Z"
                      fill="#9F57D4"
                    />
                  </svg>
                </div>
                <span>Services</span>
              </div>

              {/* Right */}
              <div className="flex items-center gap-3">
                {/* Search */}
                <div
                  className="relative"
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                >
                  <input
                    type="text"
                    placeholder="Search..."
                    value={serviceSearch}
                    onChange={(e) => handleServiceSearch(e.target.value)}
                    className="border border-gray-200 rounded-md py-1 px-3 text-sm focus:outline-none w-40 pr-8"
                  />
                  <svg
                    className="absolute right-2 top-2 w-4 h-4 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                </div>

                {/* Arrow */}
                <FontAwesomeIcon
                  icon={faChevronUp}
                  className={`text-[#9F57D4] transition-transform duration-300 ${serviceOpen ? '' : 'rotate-180'
                    }`}
                />
              </div>
            </button>
            <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-4 overflow-visible">
              {serviceOpen && (
                <>
                  {filteredServices.map((service) =>
                    isEditOrder ? (
                      // Edit order UI
                      <div
                        key={service.id}
                        onClick={() =>
                          !((serviceStatusMap[service.id] ?? 0) > 0) &&
                          toggleServiceSelection(service.id)
                        }
                        className={`p-3 mb-1 rounded-xl shadow-sm border border-gray-100 ${(serviceStatusMap[service.id] ?? 0) > 0
                          ? 'bg-[#FFFEFA80] text-[#1C1B1F] border-[#0000001A] cursor-default' // completed style
                          : selectedServiceIds.includes(service.id)
                            ? 'bg-tertiary border-primary cursor-pointer'
                            : 'bg-[#FFFEFA] cursor-pointer'
                          }`}
                      >
                        <div className="flex justify-between items-start mb-1 relative">
                          <div>
                            <h3 className="font-bold text-[#1A1C1E]">
                              {service.name}
                            </h3>
                            <p className="text-[#6C7689] text-base mt-1">
                              {service.feeType}
                            </p>
                          </div>

                          {(serviceStatusMap[service.id] ?? 0) > 0 ? (
                            <div className="flex items-center gap-1  bg-badge-green-bg text-badge-green border border-badge-green text-xs font-semibold px-2 py-1 rounded-full">
                              <svg
                                width="12"
                                height="12"
                                viewBox="0 0 12 12"
                                fill="none"
                              >
                                <circle cx="6" cy="6" r="6" fill="#16a34a" />
                                <path
                                  d="M3 6l2 2 4-4"
                                  stroke="white"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                              Completed
                            </div>
                          ) : (
                            selectedServiceIds.includes(service.id) && (
                              <div className="relative">
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setOpenServiceMenuId(
                                      openServiceMenuId === service.id
                                        ? null
                                        : service.id,
                                    );
                                  }}
                                  className="text-gray-400 p-1"
                                >
                                  <svg
                                    width="4"
                                    height="16"
                                    viewBox="0 0 4 16"
                                    fill="none"
                                  >
                                    <circle
                                      cx="2"
                                      cy="2"
                                      r="2"
                                      fill="#9CA3AF"
                                    />
                                    <circle
                                      cx="2"
                                      cy="8"
                                      r="2"
                                      fill="#9CA3AF"
                                    />
                                    <circle
                                      cx="2"
                                      cy="14"
                                      r="2"
                                      fill="#9CA3AF"
                                    />
                                  </svg>
                                </button>
                                {openServiceMenuId === service.id && (
                                  <div className="absolute right-0 top-7 bg-white border border-gray-200 rounded-lg shadow-lg z-20 w-44">
                                    <button
                                      type="button"
                                      className="flex items-center gap-2 w-full px-3 py-2 text-red-500 hover:bg-red-50 rounded-lg"
                                      onClick={() =>
                                        handleSplitViewServiceDelete(
                                          service.id,
                                          currentCustomizingId,
                                        )
                                      }
                                    >
                                      <FontAwesomeIcon icon={faTrashCan} />
                                      <span className="font-medium">
                                        Remove Service
                                      </span>
                                    </button>
                                  </div>
                                )}
                              </div>
                            )
                          )}
                        </div>

                        {/* Color dropdown */}
                        {service.colors === 1 &&
                          !((serviceStatusMap[service.id] ?? 0) > 0) && (
                            <div
                              className="relative mb-2"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <button
                                onClick={() => {
                                  setOpenColorServiceId(
                                    openColorServiceId === service.id
                                      ? null
                                      : service.id,
                                  );
                                }}
                                type="button"
                                className="w-full px-3 py-1 border border-gray-300 bg-white rounded-md flex justify-between items-center hover:border-gray-400 transition-colors"
                              >
                                <span className="truncate max-w-[100%] overflow-hidden">
                                  {selectedColors[service.id]
                                    ? colors.find(
                                      (c) =>
                                        c.id === selectedColors[service.id],
                                    )?.color
                                    : 'Select a Color'}
                                </span>
                                <FontAwesomeIcon
                                  icon={faChevronUp}
                                  className="text-gray-400 text-sm"
                                />
                              </button>
                              {openColorServiceId === service.id && (
                                <div className="absolute left-0 right-0 mt-1 bg-white border border-gray-300 rounded shadow-md z-10">
                                  <div className="flex justify-between items-center p-2 border-b">
                                    <span className="font-semibold">
                                      Choose Color
                                    </span>
                                    <button
                                      type="button"
                                      onClick={() =>
                                        setOpenColorServiceId(null)
                                      }
                                      className="text-gray-500"
                                    >
                                      ✕
                                    </button>
                                  </div>
                                  <div className="overflow-y-auto max-h-80">
                                    {colors.length > 0 ? (
                                      colors.map((color) => (
                                        <button
                                          key={color.id}
                                          type="button"
                                          className="w-full p-2 text-left cursor-pointer border border-transparent hover:bg-[#f1f8ed] hover:border-primary"
                                          onClick={() =>
                                            handleColorSelect(
                                              service.id,
                                              color.id,
                                            )
                                          }
                                        >
                                          {color.color}
                                        </button>
                                      ))
                                    ) : (
                                      <div className="p-2 text-gray-500">
                                        No colors available
                                      </div>
                                    )}
                                  </div>
                                  <div className="flex justify-end border-t p-2 space-x-2">
                                    <button
                                      type="button"
                                      className="mr-3 text-primary-500"
                                      onClick={() =>
                                        setOpenColorServiceId(null)
                                      }
                                    >
                                      Cancel
                                    </button>
                                    <button
                                      type="button"
                                      className="bg-primary text-white px-3 py-1 rounded"
                                    >
                                      Save
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}

                        <div className="font-bold text-lg">
                          {' '}
                          ${service.rate} / ft
                        </div>
                        {/* Show deposit amount if there's any */}
                        {(() => {
                          const mappedServiceId = getMappedServiceId(
                            service.id,
                          );

                          const depositAmount = getServiceDeposit(
                            mappedServiceId,
                            customerId,
                            currentCustomizingId,
                          );

                          if (!depositAmount) return null;

                          return (
                            <div className="flex justify-between items-center mt-2 pt-2 bg-white border-t border-gray-100">
                              <span className="text-[#6C7689]">Deposit</span>
                              <span className="font-bold">
                                ${depositAmount}
                              </span>
                            </div>
                          );
                        })()}
                      </div>
                    ) : (
                      // Create order UI
                      <div
                        key={service.id}
                        onClick={() => toggleServiceSelection(service.id)}
                        className={`p-3 rounded-xl shadow-sm border border-gray-100 ${selectedServiceIds.includes(service.id)
                          ? 'bg-tertiary border-primary'
                          : 'bg-[#FFFEFA]'
                          }`}
                      >
                        <div className="mb-1 md:mb-1">
                          <h3 className="font-bold text-[#1A1C1E]">
                            {service.name}
                          </h3>
                          <p className="text-[#6C7689] text-base mt-1">
                            {service.feeType}
                          </p>
                        </div>
                        {service.colors === 1 && (
                          <div
                            className="relative mb-2"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <button
                              onClick={() => {
                                setOpenColorServiceId(
                                  openColorServiceId === service.id
                                    ? null
                                    : service.id,
                                );
                              }}
                              type="button"
                              className="w-full px-3 py-1 border border-gray-300 bg-white rounded-md flex justify-between items-center hover:border-gray-400 transition-colors"
                            >
                              <span className="truncate max-w-[100%] overflow-hidden">
                                {selectedColors[service.id]
                                  ? colors.find(
                                    (c) =>
                                      c.id === selectedColors[service.id],
                                  )?.color
                                  : 'Select a Color'}
                              </span>

                              <FontAwesomeIcon
                                icon={faChevronUp}
                                className="text-gray-400 text-sm"
                              />
                            </button>
                            {openColorServiceId === service.id && (
                              <div className="absolute left-0 right-0 mt-1 bg-white border border-gray-300 rounded shadow-md z-10">
                                <button
                                  type="button"
                                  className="w-full h-full"
                                />
                                <div className="flex justify-between items-center p-2 border-b">
                                  <span className="font-semibold">
                                    Choose Color
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() => setOpenColorServiceId(null)}
                                    className="text-gray-500"
                                  >
                                    ✕
                                  </button>
                                </div>
                                <div className="overflow-y-auto max-h-80">
                                  {colors.length > 0 ? (
                                    colors.map((color) => (
                                      <button
                                        key={color.id}
                                        type="button"
                                        className="w-full p-2 text-left cursor-pointer border border-transparent hover:bg-[#f1f8ed] hover:border-primary"
                                        onClick={() =>
                                          handleColorSelect(
                                            service.id,
                                            color.id,
                                          )
                                        }
                                      >
                                        {color.color}
                                      </button>
                                    ))
                                  ) : (
                                    <div className="p-2 text-gray-500">
                                      No colors available
                                    </div>
                                  )}
                                </div>
                                <div className="flex justify-end border-t p-2 space-x-2">
                                  <button
                                    type="button"
                                    className="mr-3 text-primary-500"
                                    onClick={() => setOpenColorServiceId(null)}
                                  >
                                    Cancel
                                  </button>
                                  <button
                                    type="button"
                                    className="bg-primary text-white px-3 py-1 rounded"
                                  >
                                    Save
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                        <div className="font-bold">${service.rate}</div>
                      </div>
                    ),
                  )}
                </>
              )}
            </div>
          </div>

          {/* Accordion Product */}
          <div className="rounded-md shadow-sm mb-3 mt-4">
            <button
              onClick={() => setProductOpen(!productOpen)}
              type="button"
              className="w-full flex justify-between items-center bg-[#FFFEFA] border border-[#AD67491A] p-2 font-semibold text-lg outline-none"
            >
              {/* Left Side */}
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#F9E6DF]">
                  <svg
                    width="20"
                    height="16"
                    viewBox="0 0 21 18"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M19.0469 8.05V16C19.0469 16.55 18.8511 17.0208 18.4594 17.4125C18.0678 17.8042 17.5969 18 17.0469 18H3.04694C2.49694 18 2.02611 17.8042 1.63444 17.4125C1.24277 17.0208 1.04694 16.55 1.04694 16V8.05C0.663606 7.7 0.367772 7.25 0.159439 6.7C-0.0488946 6.15 -0.0530612 5.55 0.146939 4.9L1.19694 1.5C1.33027 1.06667 1.56777 0.708333 1.90944 0.425C2.25111 0.141667 2.64694 0 3.09694 0H16.9969C17.4469 0 17.8386 0.1375 18.1719 0.4125C18.5053 0.6875 18.7469 1.05 18.8969 1.5L19.9469 4.9C20.1469 5.55 20.1428 6.14167 19.9344 6.675C19.7261 7.20833 19.4303 7.66667 19.0469 8.05ZM12.2469 7C12.6969 7 13.0386 6.84583 13.2719 6.5375C13.5053 6.22917 13.5969 5.88333 13.5469 5.5L12.9969 2H11.0469V5.7C11.0469 6.05 11.1636 6.35417 11.3969 6.6125C11.6303 6.87083 11.9136 7 12.2469 7ZM7.74694 7C8.13027 7 8.44277 6.87083 8.68444 6.6125C8.92611 6.35417 9.04694 6.05 9.04694 5.7V2H7.09694L6.54694 5.5C6.48027 5.9 6.56777 6.25 6.80944 6.55C7.05111 6.85 7.36361 7 7.74694 7ZM3.29694 7C3.59694 7 3.85944 6.89167 4.08444 6.675C4.30944 6.45833 4.44694 6.18333 4.49694 5.85L5.04694 2H3.09694L2.09694 5.35C1.99694 5.68333 2.05111 6.04167 2.25944 6.425C2.46777 6.80833 2.81361 7 3.29694 7ZM16.7969 7C17.2803 7 17.6303 6.80833 17.8469 6.425C18.0636 6.04167 18.1136 5.68333 17.9969 5.35L16.9469 2H15.0469L15.5969 5.85C15.6469 6.18333 15.7844 6.45833 16.0094 6.675C16.2344 6.89167 16.4969 7 16.7969 7ZM3.04694 16H17.0469V8.95C16.9636 8.98333 16.9094 9 16.8844 9H16.7969C16.3469 9 15.9511 8.925 15.6094 8.775C15.2678 8.625 14.9303 8.38333 14.5969 8.05C14.2969 8.35 13.9553 8.58333 13.5719 8.75C13.1886 8.91667 12.7803 9 12.3469 9C11.8969 9 11.4761 8.91667 11.0844 8.75C10.6928 8.58333 10.3469 8.35 10.0469 8.05C9.76361 8.35 9.43444 8.58333 9.05944 8.75C8.68444 8.91667 8.28027 9 7.84694 9C7.36361 9 6.92611 8.91667 6.53444 8.75C6.14277 8.58333 5.79694 8.35 5.49694 8.05C5.14694 8.4 4.80111 8.64583 4.45944 8.7875C4.11777 8.92917 3.73027 9 3.29694 9H3.18444C3.14277 9 3.09694 8.98333 3.04694 8.95V16Z"
                      fill="#AD6749"
                    />
                  </svg>
                </div>
                <span>Products</span>
              </div>

              {/* Right Side */}
              <div className="flex items-center gap-3">
                {!isEditOrder && (
                  <>
                    {/* Search */}
                    <div
                      className="relative"
                      onClick={(e) => e.stopPropagation()}
                      onMouseDown={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                    >
                      <input
                        type="text"
                        placeholder="Search..."
                        value={productSearch}
                        onChange={(e) => handleProductSearch(e.target.value)}
                        className="border border-gray-200 rounded-md py-1 px-3 text-sm focus:outline-none w-40 pr-8"
                      />
                      <svg
                        className="absolute right-2 top-2 w-4 h-4 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                        />
                      </svg>
                    </div>
                  </>
                )}
                {/* Arrow */}
                <FontAwesomeIcon
                  icon={faChevronUp}
                  className={`text-[#AD6749] transition-transform duration-300 ${productOpen ? '' : 'rotate-180'
                    }`}
                />
              </div>
            </button>
            {/* PRODUCT CARD */}
            <div>
              {productOpen && (
                <>
                  {/* Added Products List */}
                  {addedProducts.length > 0 && (
                    <div
                      className={
                        isEditOrder
                          ? 'grid grid-cols-1 gap-4 mt-3'
                          : 'mt-3 space-y-2'
                      }
                    >
                      {addedProducts.map(
                        (addedProduct: ProductDraft, index: number) => {
                          const masterProduct = masterProducts.find(
                            (p) => p.id === addedProduct.butcherProductId,
                          );
                          if (!masterProduct) return null;

                          if (isEditOrder) {
                            const isCompleted =
                              (productStatusMap[addedProduct.productId ?? -1] ??
                                0) > 0;

                            // Edit order — card based UI
                            return (
                              <div
                                key={index}
                                className="border border-[#0000001A] rounded-lg p-2 overflow-hidden"
                              >
                                <div className="bg-[#FDF5F2] p-3 border-b border-gray-100 flex items-center justify-between">
                                  <h3 className="font-semibold text-[#1C1B1F]">
                                    {masterProduct?.name}
                                  </h3>
                                  <div className="flex items-center gap-2">
                                    {isCompleted ? (
                                      <div className="flex items-center gap-1 bg-badge-green-bg text-badge-green border border-badge-green text-xs font-semibold px-2 py-1 rounded-full">
                                        <svg
                                          width="12"
                                          height="12"
                                          viewBox="0 0 12 12"
                                          fill="none"
                                        >
                                          <circle
                                            cx="6"
                                            cy="6"
                                            r="6"
                                            fill="#16a34a"
                                          />
                                          <path
                                            d="M3 6l2 2 4-4"
                                            stroke="white"
                                            strokeWidth="1.5"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                          />
                                        </svg>
                                        Completed
                                      </div>
                                    ) : (
                                      <>
                                        {/* Edit */}
                                        <button
                                          type="button"
                                          className="px-2 py-1 text-sm text-[#4B5563] bg-white rounded"
                                          onClick={() =>
                                            showConfirmationToast(
                                              'Are you sure you want to edit product?',
                                              'Edit Product',
                                              'info',
                                              () => {
                                                setSelectedProduct(
                                                  addedProduct,
                                                );
                                                setSelectedProductIndex(index);
                                                setEditProductModal(true);
                                                setProductType({
                                                  productType:
                                                    masterProduct?.productType ??
                                                    1,
                                                  minValue:
                                                    masterProduct?.minQuantity ??
                                                    masterProduct?.weight ??
                                                    1,
                                                  allowFatContentFlag:
                                                    masterProduct?.allowFatContentFlag ??
                                                    0,
                                                  allowAddOnFlag:
                                                    masterProduct?.allowAddOnFlag ??
                                                    0,
                                                  upchargeIngredientsFlag:
                                                    masterProduct?.upchargeIngredientsFlag ??
                                                    0,
                                                });
                                              },
                                              'Edit',
                                              () => { },
                                              'Cancel',
                                            )
                                          }
                                        >
                                          <svg
                                            width="12"
                                            height="14"
                                            viewBox="0 0 14 14"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                          >
                                            <path
                                              d="M1.55556 12.4444H2.66389L10.2667 4.84167L9.15833 3.73333L1.55556 11.3361V12.4444ZM0 14V10.6944L10.2667 0.447222C10.4222 0.30463 10.594 0.194444 10.7819 0.116667C10.9699 0.0388889 11.1676 0 11.375 0C11.5824 0 11.7833 0.0388889 11.9778 0.116667C12.1722 0.194444 12.3407 0.311111 12.4833 0.466667L13.5528 1.55556C13.7083 1.69815 13.8218 1.86667 13.8931 2.06111C13.9644 2.25556 14 2.45 14 2.64444C14 2.85185 13.9644 3.04954 13.8931 3.2375C13.8218 3.42546 13.7083 3.59722 13.5528 3.75278L3.30556 14H0ZM9.70278 4.29722L9.15833 3.73333L10.2667 4.84167L9.70278 4.29722Z"
                                              fill="#4D545C"
                                            />
                                          </svg>
                                        </button>
                                        {/* Duplicate */}
                                        <button
                                          type="button"
                                          className="px-2 py-1 text-sm text-[#4B5563] bg-white rounded"
                                          onClick={() =>
                                            showConfirmationToast(
                                              'Are you sure you want to duplicate product?',
                                              'Duplicate Product',
                                              'info',
                                              () => {
                                                setSelectedProduct(
                                                  addedProduct,
                                                );
                                                setCopyProductModal(true);
                                                setProductType({
                                                  productType:
                                                    masterProduct?.productType ??
                                                    1,
                                                  minValue:
                                                    masterProduct?.minQuantity ??
                                                    masterProduct?.weight ??
                                                    1,
                                                  allowFatContentFlag:
                                                    masterProduct?.allowFatContentFlag ??
                                                    0,
                                                  allowAddOnFlag:
                                                    masterProduct?.allowAddOnFlag ??
                                                    0,
                                                  upchargeIngredientsFlag:
                                                    masterProduct?.upchargeIngredientsFlag ??
                                                    0,
                                                });
                                              },
                                              'Duplicate',
                                              undefined,
                                              'Cancel',
                                            )
                                          }
                                        >
                                          <svg
                                            width="12"
                                            height="14"
                                            viewBox="0 0 12 14"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                          >
                                            <path
                                              d="M4.2 11.2C3.815 11.2 3.48542 11.0629 3.21125 10.7887C2.93708 10.5146 2.8 10.185 2.8 9.8V1.4C2.8 1.015 2.93708 0.685417 3.21125 0.41125C3.48542 0.137083 3.815 0 4.2 0H10.5C10.885 0 11.2146 0.137083 11.4888 0.41125C11.7629 0.685417 11.9 1.015 11.9 1.4V9.8C11.9 10.185 11.7629 10.5146 11.4888 10.7887C11.2146 11.0629 10.885 11.2 10.5 11.2H4.2ZM4.2 9.8H10.5V1.4H4.2V9.8ZM1.4 14C1.015 14 0.685417 13.8629 0.41125 13.5887C0.137083 13.3146 0 12.985 0 12.6V2.8H1.4V12.6H9.1V14H1.4Z"
                                              fill="#4D545C"
                                            />
                                          </svg>
                                        </button>
                                        {/* Delete */}
                                        <button
                                          type="button"
                                          className="px-2 py-1 text-sm bg-white rounded"
                                          onClick={() =>
                                            showConfirmationToast(
                                              'Are you sure you want to delete this product?',
                                              'Delete Product',
                                              'delete',
                                              () =>
                                                handleDeleteProduct(
                                                  index,
                                                  currentCustomizingId,
                                                ),
                                              'Delete',
                                              undefined,
                                              'Cancel',
                                            )
                                          }
                                        >
                                          <FontAwesomeIcon
                                            icon={faTrashCan}
                                            className="text-[#4D545C] rounded-md cursor-pointer hover:opacity-70"
                                          />
                                        </button>
                                      </>
                                    )}
                                  </div>
                                </div>

                                <div className="p-2 space-y-2 text-sm">
                                  <hr className="border-gray-100" />
                                  <div className="flex gap-2">
                                    <span className="text-[#43474E] shrink-0">Quantity:</span>
                                    <span className="font-semibold text-[#1C1B1F]">
                                      {addedProduct.quantity}
                                      {masterProduct?.productType === 2 ? ' Qty' : ' Lbs'}
                                    </span>
                                  </div>
                                  {addedProduct.fatPercentage && Number(addedProduct.fatPercentage) !== 0 && (
                                    <div className="flex gap-2">
                                      <span className="text-[#43474E] shrink-0">Fat:</span>
                                      <span className="font-semibold text-[#1C1B1F]">{addedProduct.fatPercentage}%</span>
                                    </div>
                                  )}
                                  {addedProduct.addons?.length > 0 && (
                                    <>
                                      <div className="flex gap-2">
                                        <span className="text-[#43474E] shrink-0">
                                          Add-Ons:
                                        </span>
                                        <span className="font-semibold text-[#1C1B1F]">
                                          {addedProduct.addons
                                            .map((a: AddonsList) => a.name)
                                            .join(', ')}
                                        </span>
                                      </div>
                                      <hr className="border-gray-100" />
                                    </>
                                  )}
                                  {addedProduct.packages?.length > 0 && (
                                    <>
                                      <div className="space-y-1 text-sm text-[#43474E]">
                                        <p className="font-semibold">
                                          Packaging:
                                        </p>
                                        {addedProduct.packages.map(
                                          (pkg, pkgIdx: number) => (
                                            <div
                                              key={pkgIdx}
                                              className="flex items-center"
                                            >
                                              <span>{pkg.packageType}</span>
                                              <span className="mx-2">-</span>
                                              <span>
                                                Pkg Split:{' '}
                                                <span className="font-bold">
                                                  {normalizePackagePercentageNumber(
                                                    pkg.percentage,
                                                  )}
                                                  %
                                                </span>
                                              </span>
                                            </div>
                                          ),
                                        )}
                                      </div>
                                      <hr className="border-gray-100" />
                                    </>
                                  )}
                                  {addedProduct.notes && (
                                    <div>
                                      <p className="font-semibold text-[#1C1B1F]">
                                        Packaging Notes:
                                      </p>
                                      <p className="mt-1 text-[#1C1B1F]">
                                        {addedProduct.notes}
                                      </p>
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          }
                          return <></>;
                        },
                      )}
                    </div>
                  )}

                  {/* Product selection — hidden for edit order */}
                  {!isEditOrder && (
                    <>
                      {currentProductDetail == null ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-stretch mt-3">
                          {/* Product */}
                          {filteredProducts.map((product) => {
                            // Product count
                            const count = addedProducts.filter(
                              (item) => item.productId === product.id,
                            ).length;
                            return (
                              <div
                                key={product.id}
                                className="flex flex-col gap-3 h-full"
                              >
                                {/* Product Info */}
                                <div
                                  className={`relative p-4 rounded-lg border bg-[#FFFEFA] border-[#0000001A] shadow-md flex flex-col h-full`}
                                >
                                  {count > 0 && (
                                    <div className="absolute top-2 right-2 bg-primary text-white text-xs font-semibold w-6 h-6 rounded-full flex items-center justify-center shadow">
                                      {count}
                                    </div>
                                  )}

                                  <div>
                                    <h3 className="font-semibold text-[#1A1C1E] text-lg">
                                      {product.name}
                                    </h3>

                                    <p className="text-[#555F71] text-sm mt-1">
                                      {product.productType ===
                                        ProductType.QUANTITY
                                        ? `Min Quantity: ${product.minQuantity}`
                                        : `Min Weight: ${product.weight} ${WeightType.LBS}`}
                                    </p>
                                  </div>

                                  <div className="mt-auto font-medium text-[#1A1C1E] pt-6">
                                    ${product.price}/{WeightType.LBS}
                                  </div>
                                </div>

                                {/* Button */}
                                <button
                                  type="button"
                                  onClick={() => handleSelectProduct(product)}
                                  className="bg-primary text-white font-medium py-3 rounded-md"
                                >
                                  Add
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="mt-2 bg-white p-4 rounded-xl border border-gray-200">
                          {/* Header */}
                          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
                            {/* LEFT — Product Info */}
                            <div className="p-4 bg-[#F9F3F1] rounded-lg border border-gray-200">
                              <div className="flex justify-between items-start">
                                <div>
                                  <h3 className="font-semibold xl:text-xl text-[#1A1C1E]">
                                    {currentProductDetail?.name}
                                  </h3>

                                  <p className="text-sm text-gray-500 mt-1">
                                    {currentProductDetail?.productType ===
                                      ProductType.QUANTITY
                                      ? `Min Quantity: ${currentProductDetail?.minQuantity}`
                                      : `Min Weight: ${currentProductDetail?.weight} ${WeightType.LBS}`}{' '}
                                  </p>
                                </div>

                                <div className="font-bold text-[#1A1C1E]">
                                  ${currentProductDetail.price}/{WeightType.LBS}
                                </div>
                              </div>

                              {/* Fat Percentage */}
                              {currentProductDetail.allowFatContentFlag ===
                                1 && (
                                  <div className="flex items-center gap-4 mt-4">
                                    <span className="font-medium">
                                      Fat Percentage:
                                    </span>
                                    <div className="relative">
                                      <input
                                        value={productDraft?.fatPercentage || ''}
                                        onChange={(e) =>
                                          setProductDraft((prev: any) => ({
                                            ...prev,
                                            fatPercentage: e.target.value,
                                          }))
                                        }
                                        type="string"
                                        min="0"
                                        max="100"
                                        className={`w-full max-w-[160px] bg-white border ${errors.fatPercentage ? 'border-red-500' : 'border-gray-300'} rounded py-1.5 pl-3 pr-8 font-bold text-lg
                      shadow-sm outline-none focus:ring-1 focus:ring-primary`}
                                      />
                                      {errors.fatPercentage && (
                                        <p className="text-red-500 text-xs mt-1">
                                          {errors.fatPercentage}
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                )}
                            </div>

                            {/* RIGHT — Measurement Type */}
                            <div>
                              <h4 className="font-medium mb-3 text-lg">
                                Measurement Type
                              </h4>
                              <label className="flex items-center gap-2 mb-2">
                                {currentProductDetail?.productType ==
                                  ProductType.QUANTITY
                                  ? 'Quantity'
                                  : 'Weight'}
                              </label>

                              <input
                                type="number"
                                max="100"
                                value={productDraft?.quantity ?? ''}
                                onChange={(e) =>
                                  setProductDraft(
                                    (
                                      prev: ProductDraft | null,
                                    ): ProductDraft | null =>
                                      prev
                                        ? {
                                          ...prev,
                                          productId: prev.productId ?? null,
                                          quantity:
                                            e.target.value === ''
                                              ? ''
                                              : Number(e.target.value),
                                        }
                                        : prev,
                                  )
                                }
                                placeholder={
                                  currentProductDetail?.productType ==
                                    ProductType.QUANTITY
                                    ? 'Quantity'
                                    : 'Weight'
                                }
                                className={`w-full border ${errors.quantity ? 'border-red-500' : 'border-gray-300'} rounded-md p-2 text-sm mb-2 shadow-sm outline-none focus:ring-1 focus:ring-primary`}
                              />
                              {errors.quantity && (
                                <p className="text-red-500 text-xs mt-1">
                                  {errors.quantity}
                                </p>
                              )}

                              {/* Do not remove this code. The cuts of meat/instructions will be added in the future */}
                              {/* <div className="border border-green-600 bg-green-50 rounded-md p-3">
            <label className="flex items-center gap-2 mb-2">
              <input type="radio" name="measure" className="accent-primary" defaultChecked />
              Cuts of Meat / Instructions
            </label>

            <input type="text" placeholder="Add Cuts of Meat/Instructions"
              className="w-full border rounded-md p-2 text-sm" />
          </div> */}
                            </div>
                          </div>

                          {/* --- ADD-ONS SECTION --- */}
                          {/* show this sections if product has allowAddOnFlag key as 1 */}
                          <div className="mt-8">
                            {currentProductDetail?.allowAddOnFlag === 1 && (
                              <div className="bg-[#FFFEFA] border border-[#0000001A] rounded-lg ">
                                <div
                                  className="flex items-center justify-between p-3 rounded-t-lg cursor-pointer"
                                  onClick={() => setOpenAddons(!openAddons)}
                                >
                                  <h3 className="font-bold text-[#333] text-lg">
                                    Add - Ons
                                  </h3>
                                  <div className="flex items-center gap-2">
                                    <div
                                      className="relative"
                                      onClick={(e) => e.stopPropagation()}
                                      onMouseDown={(e) => e.stopPropagation()}
                                      onKeyDown={(e) => e.stopPropagation()}
                                    >
                                      <input
                                        type="text"
                                        value={searchKey}
                                        onChange={(e) =>
                                          handleAddonSearch(e.target.value)
                                        }
                                        placeholder="Search.."
                                        className="border border-gray-200 rounded-md py-1 px-3 text-sm focus:outline-none w-40"
                                      />
                                      <svg
                                        className="absolute right-2 top-2 w-4 h-4 text-gray-400"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                      >
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          strokeWidth="2"
                                          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                                        />
                                      </svg>
                                    </div>
                                    <svg
                                      className="w-5 h-5 text-gray-500"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth="2"
                                        d="M19 9l-7 7-7-7"
                                      />
                                    </svg>
                                  </div>
                                </div>
                                {openAddons && (
                                  <div className="border border-t-0 bg-[#FFFEFA] border-[#0000001A] rounded-b-lg px-3 py-2 lg:px-6 lg:py-3">
                                    <div className="grid grid-cols-2 gap-x-3 gap-y-0 lg:gap-x-10 lg:gap-y-1.5">
                                      {filteredAddons.map((addon) => {
                                        return (
                                          <label
                                            key={addon.id}
                                            className="flex items-center justify-between cursor-pointer py-1 lg:py-2 min-w-0"
                                          >
                                            <span
                                              className="text-[#333] font-medium text-[11px] md:text-xs lg:text-xs xl:text-base truncate flex-1 min-w-0"
                                              title={addon.name}
                                            >
                                              {addon.name}
                                            </span>

                                            <div className="flex items-center gap-1 xl:gap-3 shrink-0 ml-1">
                                              {Number(
                                                currentProductDetail?.upchargeIngredientsFlag ??
                                                0,
                                              ) === 1 && (
                                                  <span className="text-[11px] md:text-xs lg:text-xs xl:text-base font-semibold text-gray-600 whitespace-nowrap">
                                                    +${addon.price}/Lbs
                                                  </span>
                                                )}
                                              <input
                                                type="checkbox"
                                                className="w-3 h-3 md:w-3.5 md:h-3.5 lg:w-4 lg:h-4 accent-primary cursor-pointer shrink-0"
                                                checked={productDraft?.addons?.some(
                                                  (a: AddonsList) =>
                                                    a.id === addon.id,
                                                )}
                                                onChange={() =>
                                                  toggleAddon(addon)
                                                }
                                              />
                                            </div>
                                          </label>
                                        );
                                      })}
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}

                            {/* Packaging Sub-Section */}
                            <div className="mt-4">
                              <h4 className="font-semibold text-[#1C1B1F] mb-3 text-lg">
                                Packaging
                              </h4>

                              {/* Added Packaging Item Row */}
                              {selectedPackages.map((packaging, index) => {
                                return (
                                  <div
                                    key={packaging.id}
                                    className="relative flex gap-2 items-center mb-4"
                                  >
                                    <div className="flex-grow flex items-center justify-between p-3 border border-gray-200 rounded-lg bg-white">
                                      <span className="text-gray-700 font-medium">
                                        {packaging.packageType}
                                      </span>

                                      <div className="flex items-center gap-6">
                                        <span className="text-gray-500 text-sm">
                                          {packaging.packageSize} lbs
                                        </span>

                                        <div className="flex items-center gap-1 text-gray-600 font-medium">
                                          <span>
                                            {`+ $${packaging?.price?.toFixed(2)}`}{' '}
                                            / {packaging?.unit}
                                          </span>

                                          <FontAwesomeIcon
                                            icon={faChevronDown}
                                            className="text-[#A09CAB] text-sm cursor-pointer"
                                            onClick={() =>
                                              setDropdownOpenIndex(
                                                dropdownOpenIndex === index
                                                  ? null
                                                  : index,
                                              )
                                            }
                                          />
                                        </div>
                                      </div>
                                    </div>
                                    {selectedPackages.length > 1 && (
                                      <>
                                        <div className="relative w-[80px] ml-2">
                                          <input
                                            type="text"
                                            inputMode="numeric"
                                            pattern="[0-9]*"
                                            className={`h-12 w-full pr-5 pl-2 border rounded-md focus:outline-primary text-center ${percentageError
                                              ? 'border-red-500'
                                              : 'border-gray-300'
                                              }`}
                                            value={packaging.percentage ?? ''}
                                            onKeyDown={
                                              handlePercentageKeyDown
                                            }
                                            onChange={(e) =>
                                              handlePercentageChange(
                                                index,
                                                e.target.value,
                                              )
                                            }
                                            onBlur={(e) =>
                                              handlePercentageBlur(
                                                e.target.value,
                                              )
                                            }
                                          />
                                          <span className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none">
                                            %
                                          </span>
                                        </div>
                                      </>
                                    )}
                                    {/* Delete Button */}
                                    <button
                                      type="button"
                                      disabled={selectedPackages.length <= 1}
                                      className={`p-3 border border-[#D4D4D4] rounded-lg text-[#4D545C] ${selectedPackages.length <= 1 ? 'opacity-40 cursor-not-allowed' : ''}`}
                                      onClick={() =>
                                        handleDeletePackage(packaging.id)
                                      }
                                    >
                                      <FontAwesomeIcon icon={faTrashCan} />
                                    </button>

                                    {/* Packages Dropdown */}
                                    {dropdownOpenIndex === index &&
                                      remainingPackages.length > 0 && (
                                        <div className="absolute left-0 top-full mt-1 w-full bg-white border border-gray-200 rounded-lg shadow z-10">
                                          {/* Header */}
                                          <div className="grid grid-cols-3 gap-2 p-3 border-b text-xs font-semibold bg-[#F0F0F0] uppercase">
                                            <span>Title</span>
                                            <span>Size in lbs</span>
                                            <span>Upcharge</span>
                                          </div>

                                          {/* Package List */}
                                          {remainingPackages.map((pack) => (
                                            <div
                                              key={pack.id}
                                              onClick={() =>
                                                handleSelectPackage(pack)
                                              }
                                              className="grid grid-cols-3 gap-2 p-3 hover:bg-gray-100 cursor-pointer text-sm"
                                            >
                                              <span className="text-gray-700">
                                                {pack.packageType}
                                              </span>
                                              <span className="text-gray-500">
                                                {pack.packageSize} lbs
                                              </span>
                                              <span className="text-gray-600">{`+ $${pack.price.toFixed(2)} / ${pack.unit}`}</span>
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                  </div>
                                );
                              })}

                              {/* Action Button */}
                              {selectedPackages.length < 2 && (
                                <button
                                  type="button"
                                  onClick={() => setDropdownOpenIndex(0)}
                                  className="w-full py-2.5 border border-primary text-primary font-bold rounded-lg"
                                >
                                  Add Packaging
                                </button>
                              )}
                            </div>
                          </div>
                          {/* Notes */}
                          <div className="mt-4">
                            <h4 className="font-medium mb-2 text-lg">Notes</h4>
                            <textarea // onChange={()=> handlePackagingNotesChange(2, "wewr")}
                              value={productDraft?.notes || ''}
                              onChange={(e) =>
                                setProductDraft(
                                  (
                                    prev: ProductDraft | null,
                                  ): ProductDraft | null =>
                                    prev
                                      ? {
                                        ...prev,
                                        productId: prev.productId ?? null,
                                        notes: e.target.value,
                                      }
                                      : prev,
                                )
                              }
                              name="packagingNotes"
                              placeholder="Add notes or description..."
                              className="w-full border rounded-md p-3"
                            />
                          </div>

                          {/* Footer buttons */}
                          <div className="flex justify-end gap-4 mt-6">
                            <button
                              type="button"
                              className="text-primary font-bold"
                              onClick={() => {
                                setCurrentProductDetail(null);
                                const defaultPack = MasterPackageExtenss.filter(
                                  (p) => p.defaultFlag === 1,
                                ).map((p) => ({
                                  ...p,
                                  name: p.packageType,
                                  percentage: '100',
                                }));
                                setSelectedPackages(defaultPack);
                              }}
                            >
                              Discard
                            </button>

                            <button
                              type="button"
                              onClick={() =>
                                handleAddedProducts(currentProductDetail)
                              }
                              className="md:w-[120px] w-[140px] bg-[#0B5D3B] text-white px-6 py-2 rounded-md"
                            >
                              Add
                            </button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                  {/* Add New Product button */}
                  {isEditOrder && !hasCompletedProductInEditOrder && (
                    <button
                      type="button"
                      onClick={() => setAddNewProduct(true)}
                      className="w-full py-2.5 border border-primary text-white bg-primary font-semibold rounded-lg mt-3"
                    >
                      + Add New Product
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
        {editProductModal && selectedProduct && (
          <EditProductModal
            product={selectedProduct}
            allAddons={addons}
            allPackages={MasterPackageExtenss}
            onClose={() => {
              setEditProductModal(false);
              setSelectedProduct(null);
              setSelectedProductIndex(null);
            }}
            onSave={(updatedProduct) => {
              if (selectedProductIndex !== null) {
                handleEditProduct(
                  updatedProduct,
                  selectedProductIndex,
                  currentCustomizingId,
                );
              }
              setEditProductModal(false);
              setSelectedProduct(null);
              setSelectedProductIndex(null);
            }}
            productTypeInfo={productType}
          />
        )}

        {copyProductModal && selectedProduct && (
          <CopyProductModal
            product={selectedProduct}
            allAddons={addons}
            allPackages={MasterPackageExtenss}
            onClose={() => {
              setCopyProductModal(false);
              setSelectedProduct(null);
            }}
            onSave={(updatedProduct) => {
              handleDuplicateProduct(updatedProduct, currentCustomizingId);
              setCopyProductModal(false);
              setSelectedProduct(null);
            }}
            productTypeInfo={productType}
          />
        )}
        <OrderSplitView
          addedProducts={addedProducts}
          selectedServiceIds={selectedServiceIds}
          handleEditProduct={handleEditProduct}
          handleDeleteProduct={handleDeleteProduct}
          handleDuplicateProduct={handleDuplicateProduct}
          onUpdateCompletedSet={handleUpdateCompletedSet}
          handleDeleteService={handleSplitViewServiceDelete}
          // onUpdateCompletedSetServices={handleUpdateCompletedSetServices}
          isEditOrder={isEditOrder}
        />
      </div>
    </div>
  );
};

export default SpeciesDetails;
